(() => {
  const FILTER_PARAM_NAMES = {
    type: "filter.p.product_type",
    scentProfile: "filter.p.m.custom.scent_profile",
    note: "filter.p.m.custom.perfumery_note",
    aromatherapy: "filter.p.m.custom.aromatherapy",
    organic:
      "filter.p.m.custom.custom_product_badge",
    olfactive: "filter.p.tag",
    priceMinimum: "filter.v.price.gte",
    priceMaximum: "filter.v.price.lte"
  };

  const TYPE_LABELS = {
    "essential-oils": "Æterisk olie",
    absolutes: "Absolut",
    "co2-extracts": "CO2-ekstrakt",
    isolates: "Naturligt duftstof",
    "perfumery-bases": "Naturlig parfumebase",
    blends: "Blend",
    resins: "Resin og Røgelse"
  };

  const TYPE_ALIASES = {
    "essential-oils": ["Æterisk olie", "Essential oil"],
    absolutes: ["Absolut", "Absolute"],
    "co2-extracts": ["CO2-ekstrakt", "CO2 extract"],
    isolates: ["Naturligt duftstof", "Natural aroma chemical", "Natural isolate"],
    "perfumery-bases": ["Naturlig parfumebase", "Natural perfumery base"],
    blends: ["Blend"],
    resins: ["Resin og Røgelse", "Resin and incense"]
  };

  /*
   * Kun disse kataloggrupper må vælges automatisk, og kun når hele
   * søgestrengen er et eksakt term- eller synonymmatch i search map.
   * Præfikser og fuzzy matches må aldrig vælge et filter.
   *
   * Note og Olfactive character er bevidst ikke medtaget her. De kan
   * bidrage med forslag/produkter, men vælges kun af brugeren selv.
   */
  const AUTO_SELECT_FACET_KINDS = new Set([
    "type",
    "scent_profile",
    "aromatherapy",
    "organic",
    /*
     * En enkelt Olfactive character må ikke automatisk erstatte en
     * produktsøgning. Et ord som "rose" kan både være et produktnavn
     * og et tag. Forslaget vises stadig, men titelmatch får lov at vinde.
     */
  ]);

  class EsenseraFullSearch {
    constructor(root) {
      this.root = root;
      this.productContainer = root.querySelector("#ProductContainer");
      this.form = root.querySelector("[data-esensera-main-search-form]");
      this.input = this.form?.querySelector('input[name="q"]');
      this.productTemplate = document.querySelector(
        "[data-esensera-product-group-template]"
      );

      this.collectionContextContainer = root.querySelector(
        "[data-esensera-search-collection-context]"
      );

      this.emptyStateContainer = root.querySelector(
        "[data-esensera-search-empty-state]"
      );

      this.resultsStateContainer = root.querySelector(
        "[data-esensera-search-results-state]"
      );

      /*
       * Gem Shopifys server-renderede side- og artikelresultater,
       * før den samlede søgemotor erstatter ProductContainer.
       *
       * De bruges som et ekstra indholdslag, når søgningen ikke
       * returnerer produkter.
       */
      this.nativeContentMatches =
        this.readNativeContentMatches();

      this.searchMap = this.readJson(
        "[data-esensera-search-map-page]",
        "ESENSERA search map"
      );

      this.contentIndex = this.readJson(
        "[data-esensera-search-content-index-page]",
        "ESENSERA content index"
      );

      this.config =
        this.readJson(
          "[data-esensera-full-search-config]",
          "ESENSERA full search config"
        ) || {};

      this.minimumQueryLength = Number(
        this.config.minimumQueryLength || 3
      );

      this.sectionId =
        this.config.productSectionId || "esensera-search-products";

      this.pageSize = Math.max(
        1,
        Number(this.config.pageSize || 16)
      );

      this.maximumSourcePages = Math.max(
        1,
        Number(this.config.maximumSourcePages || 20)
      );

      this.query = String(
        this.config.query || this.input?.value || ""
      ).trim();

      this.abortController = null;
      this.allProducts = [];
      this.catalogProducts = [];
      this.contentMatches = {
        pages: [],
        articles: []
      };
      this.dataReady = false;
      this.pendingFacetSearchParams = null;
      this.currentSearchParams = new URLSearchParams(
        window.location.search
      );

      this.activeFacetRefreshTimers = [];
      this.collectionContextCache = new Map();
      this.collectionContextAbortController = null;
      this.collectionContextRequestId = 0;
    }

    init() {
      if (!this.productContainer || !this.productTemplate) {
        this.root.removeAttribute(
          "data-esensera-custom-search-pending"
        );
        return;
      }

      window.esenseraFullSearchController = this;

      this.bindForm();
      this.bindCustomFacetLinks();
      this.bindBrowseFacetNavigation();

      if (!this.isQueryLongEnough(this.query)) {
        this.root.removeAttribute(
          "data-esensera-custom-search-pending"
        );
        return;
      }

      /*
       * /search er en fri søgekontekst, ikke en aktiv facet. Fjern derfor
       * straks eventuelle gamle filterparametre og Shopifys server-renderede
       * "Fjern alle"-række, før de asynkrone produktkilder er færdige.
       * Det forhindrer både et kort UI-flash og en misvisende facettilstand,
       * hvis en produktkilde mod forventning fejler.
       */
      const initialFreeSearchParams =
        new URLSearchParams(window.location.search);

      Array.from(initialFreeSearchParams.keys()).forEach((key) => {
        if (key.startsWith("filter.")) {
          initialFreeSearchParams.delete(key);
        }
      });

      initialFreeSearchParams.delete("page");
      initialFreeSearchParams.delete("section_id");

      this.currentSearchParams = initialFreeSearchParams;
      this.updateActiveFacets(initialFreeSearchParams);

      if (
        window.location.search.slice(1) !==
        initialFreeSearchParams.toString()
      ) {
        this.replaceHistory(initialFreeSearchParams);
      }

      this.render();
    }

    canHandleFacets() {
      return Boolean(
        this.root?.isConnected &&
          this.isQueryLongEnough(this.query)
      );
    }

    readJson(selector, label) {
      const element = document.querySelector(selector);

      if (!element) {
        console.warn(`${label} blev ikke fundet.`);
        return null;
      }

      try {
        return JSON.parse(element.textContent.trim());
      } catch (error) {
        console.error(`${label} kunne ikke læses:`, error);
        return null;
      }
    }

    normalizeTerm(value) {
      return String(value || "")
        .normalize("NFC")
        .toLocaleLowerCase("da-DK")
        .trim()
        .replace(/\s+/g, " ");
    }

    normalizeFuzzyTerm(value) {
      return this.normalizeTerm(value)
        .replace(/æ/g, "ae")
        .replace(/ø/g, "o")
        .replace(/å/g, "a")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\p{L}\p{N}\s]/gu, " ")
        .replace(/\s+/g, " ")
        .trim();
    }

    getNewProductsSearchEntry() {
      if (!Array.isArray(this.searchMap?.entries)) {
        return null;
      }

      return (
        this.searchMap.entries.find(
          (entry) =>
            entry?.special_mode ===
              "new_products" ||
            entry?.id ===
              "popular-new-products"
        ) || null
      );
    }

    isNewProductsQuery(value) {
      const normalizedQuery =
        this.normalizeFuzzyTerm(value);

      if (!normalizedQuery) {
        return false;
      }

      const entry =
        this.getNewProductsSearchEntry();

      if (!entry) {
        return false;
      }

      const terms = [
        entry.label,
        ...(Array.isArray(entry.terms)
          ? entry.terms
          : []),
        ...(Array.isArray(entry.submit_terms)
          ? entry.submit_terms
          : [])
      ];

      return terms.some(
        (term) =>
          this.normalizeFuzzyTerm(term) ===
          normalizedQuery
      );
    }


    tokenizeQuery(value) {
      const normalizedValue = String(value || "").normalize("NFC");
      const matches = [...normalizedValue.matchAll(/[\p{L}\p{N}]+/gu)];

      return matches
        .map((match, index) => ({
          text: this.normalizeTerm(match[0]),
          consumed: false,
          index,
          start: Number(match.index || 0),
          end: Number(match.index || 0) + match[0].length
        }))
        .filter((token) => Boolean(token.text));
    }

    normalizeIntentToken(value) {
      return this.normalizeTerm(value)
        .replace(/[^\p{L}\p{N}]/gu, "")
        .trim();
    }

    getIntentLanguageConfig() {
      return this.searchMap?.intent_language || {};
    }

    getComposableIntentEntries() {
      if (!Array.isArray(this.searchMap?.entries)) {
        return [];
      }

      return this.searchMap.entries.filter((entry) => {
        return (
          entry?.composable_intent === true &&
          Boolean(entry?.filter_param) &&
          String(entry?.filter_value || "").trim() !== ""
        );
      });
    }

    getOlfactiveIntentEntries() {
      if (!Array.isArray(this.searchMap?.entries)) {
        return [];
      }

      return this.searchMap.entries.filter((entry) => {
        return (
          entry?.kind === "olfactive_character" &&
          Boolean(entry?.filter_param) &&
          String(entry?.filter_value || "").trim() !== "" &&
          Array.isArray(entry?.terms)
        );
      });
    }

    getEntryComposableTerms(entry) {
      const terms = Array.isArray(entry?.composable_terms)
        ? entry.composable_terms
        : entry?.terms;

      return Array.isArray(terms) ? terms : [];
    }

    getEntryKey(entry) {
      const id = String(entry?.id || "").trim();

      if (id) {
        return `id:${id}`;
      }

      return [
        String(entry?.filter_param || "").trim(),
        this.normalizeTerm(entry?.filter_value)
      ].join("\u0000");
    }

    addUniqueEntry(entries, entry) {
      if (!entry) {
        return;
      }

      const key = this.getEntryKey(entry);

      if (!key || entries.some((candidate) => this.getEntryKey(candidate) === key)) {
        return;
      }

      entries.push(entry);
    }

    getUniqueEntries(entries) {
      const uniqueEntries = [];

      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        this.addUniqueEntry(uniqueEntries, entry);
      });

      return uniqueEntries;
    }

    getRemainingSearchTerm(tokens) {
      return (Array.isArray(tokens) ? tokens : [])
        .filter((token) => !token.consumed && token.text)
        .map((token) => token.text)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
    }

    isAllowedTrailingIntentPrefix(
      entry,
      comparableToken,
      spanLength,
      rawToken = ""
    ) {
      const normalizedRawToken = this.normalizeTerm(rawToken);

      /*
       * Et enkelt dansk "ø" efter et etableret produktnavn er allerede et
       * entydigt begyndende øko-signal. Bevar derfor produktresultatet fra
       * første bogstav i "øko", mens et almindeligt engelsk "o" fortsat er
       * for tvetydigt til at blive fortolket som "organic".
       */
      if (
        entry?.kind === "organic" &&
        spanLength === 1 &&
        normalizedRawToken === "ø"
      ) {
        return true;
      }

      const minimumLength = spanLength > 1 ? 1 : 2;

      if (!comparableToken || comparableToken.length < minimumLength) {
        return false;
      }

      if (entry?.kind !== "organic") {
        return true;
      }

      /*
       * Undgå at et kort, almindeligt produktord som "or..." midlertidigt
       * bliver læst som "organic". De danske øko-varianter er tydelige fra
       * to tegn, mens den engelske rod først er sikker fra "org".
       */
      return (
        comparableToken.startsWith("ok") ||
        comparableToken.startsWith("oek") ||
        comparableToken.startsWith("org")
      );
    }

    consumeTrailingProductIntentPrefixes(tokens, entries, matchedEntries) {
      if (!Array.isArray(tokens) || tokens.length < 2) {
        return false;
      }

      const candidates = [];

      entries
        .filter((entry) => ["type", "organic"].includes(entry?.kind))
        .forEach((entry) => {
          this.getEntryComposableTerms(entry).forEach((term) => {
            const termTokens = this.tokenizeQuery(term)
              .map((token) => this.normalizeFuzzyTerm(token.text))
              .filter(Boolean);

            if (!termTokens.length) {
              return;
            }

            for (let startIndex = 1; startIndex < tokens.length; startIndex += 1) {
              const span = tokens.slice(startIndex);

              if (
                span.length > termTokens.length ||
                span.some((token) => token.consumed) ||
                !tokens
                  .slice(0, startIndex)
                  .some((token) => !token.consumed && token.text)
              ) {
                continue;
              }

              const comparableSpan = span.map((token) =>
                this.normalizeFuzzyTerm(token.text)
              );

              const lastComparableToken =
                comparableSpan[comparableSpan.length - 1] || "";

              /*
               * Et færdigskrevet, men alene stående første ord i et længere
               * typeudtryk er ikke i sig selv en sikker typeintention.
               * "agermynte olie" må fx ikke blive læst som begyndelsen på
               * typen "olie blanding".
               */
              const expectedLastToken =
                termTokens[comparableSpan.length - 1] || "";

              const isBareGenericProductWord =
                comparableSpan.length === 1 &&
                this.getGenericProductWords().has(lastComparableToken) &&
                (termTokens.length > 1 ||
                  expectedLastToken !== lastComparableToken);

              if (
                isBareGenericProductWord ||
                comparableSpan.length < termTokens.length &&
                lastComparableToken === expectedLastToken
              ) {
                continue;
              }

              if (
                !this.isAllowedTrailingIntentPrefix(
                  entry,
                  lastComparableToken,
                  span.length,
                  span[span.length - 1]?.text || ""
                )
              ) {
                continue;
              }

              const isMatchingPrefix = comparableSpan.every(
                (comparableToken, offset) => {
                  const expectedToken = termTokens[offset];

                  if (!expectedToken || !comparableToken) {
                    return false;
                  }

                  if (offset === comparableSpan.length - 1) {
                    return expectedToken.startsWith(comparableToken);
                  }

                  return expectedToken === comparableToken;
                }
              );

              if (!isMatchingPrefix) {
                continue;
              }

              candidates.push({
                entry,
                startIndex,
                spanLength: span.length,
                termLength: termTokens.join("").length,
                matchedLength: comparableSpan.join("").length
              });
            }
          });
        });

      const bestCandidate = candidates.sort((candidateA, candidateB) => {
        return (
          candidateB.spanLength - candidateA.spanLength ||
          candidateB.matchedLength - candidateA.matchedLength ||
          candidateA.termLength - candidateB.termLength ||
          Number(candidateB.entry?.priority || 0) -
            Number(candidateA.entry?.priority || 0)
        );
      })[0];

      if (!bestCandidate) {
        return false;
      }

      tokens.slice(bestCandidate.startIndex).forEach((token) => {
        token.consumed = true;
      });

      this.addUniqueEntry(matchedEntries, bestCandidate.entry);
      return true;
    }

    applyCompoundIntentSuffixes(tokens, entries, matchedEntries) {
      let changed = false;

      entries.forEach((entry) => {
        const suffixes = Array.isArray(entry?.compound_suffixes)
          ? entry.compound_suffixes
              .map((suffix) => this.normalizeIntentToken(suffix))
              .filter(Boolean)
              .sort((suffixA, suffixB) => suffixB.length - suffixA.length)
          : [];

        if (!suffixes.length) {
          return;
        }

        const exclusions = new Set(
          (Array.isArray(entry?.compound_exclusions)
            ? entry.compound_exclusions
            : []
          )
            .map((value) => this.normalizeIntentToken(value))
            .filter(Boolean)
        );

        const minimumBaseLength = Math.max(
          1,
          Number(entry?.compound_min_base_length || 3)
        );

        tokens.forEach((token) => {
          if (token.consumed) {
            return;
          }

          const comparableToken = this.normalizeIntentToken(token.text);

          if (!comparableToken || exclusions.has(comparableToken)) {
            return;
          }

          const matchingSuffix = suffixes.find((suffix) => {
            if (comparableToken === suffix) {
              return true;
            }

            if (!comparableToken.endsWith(suffix)) {
              return false;
            }

            return comparableToken.length - suffix.length >= minimumBaseLength;
          });

          if (!matchingSuffix) {
            return;
          }

          this.addUniqueEntry(matchedEntries, entry);
          changed = true;

          if (comparableToken === matchingSuffix) {
            token.consumed = true;
            return;
          }

          token.text = comparableToken.slice(0, -matchingSuffix.length);
          token.consumed = !token.text;
        });
      });

      return changed;
    }

    consumeExactIntentTerms(tokens, entries, matchedEntries) {
      let changed = false;

      const candidates = entries
        .flatMap((entry) => {
          return this.getEntryComposableTerms(entry).map((term) => {
            const termTokens = this.tokenizeQuery(term)
              .map((token) => this.normalizeFuzzyTerm(token.text))
              .filter(Boolean);

            return {
              entry,
              termTokens,
              termLength: termTokens.join("").length
            };
          });
        })
        .filter((candidate) => candidate.termTokens.length)
        .sort((candidateA, candidateB) => {
          return (
            candidateB.termTokens.length - candidateA.termTokens.length ||
            candidateB.termLength - candidateA.termLength ||
            Number(candidateB.entry?.priority || 0) -
              Number(candidateA.entry?.priority || 0)
          );
        });

      const exactPhraseEntryKeys = new Map();

      candidates.forEach(({ entry, termTokens }) => {
        const phraseKey = termTokens.join("\u0000");
        const entryKey = this.getEntryKey(entry);

        if (!exactPhraseEntryKeys.has(phraseKey)) {
          exactPhraseEntryKeys.set(phraseKey, new Set());
        }

        exactPhraseEntryKeys.get(phraseKey).add(entryKey);
      });

      candidates.forEach(({ entry, termTokens }) => {
        const phraseKey = termTokens.join("\u0000");

        /*
         * Et generisk ord som fx "ekstrakt" kan med vilje tilhøre flere
         * strukturerede kategorier. I den situation må vi ikke arbitrært
         * konsumere ordet som den første facet og dermed låse søgningen.
         * Kun entydige, fulde composable-termer må blive til intent.
         */
        if ((exactPhraseEntryKeys.get(phraseKey)?.size || 0) !== 1) {
          return;
        }

        const tokenCount = termTokens.length;

        for (
          let startIndex = 0;
          startIndex <= tokens.length - tokenCount;
          startIndex += 1
        ) {
          const span = tokens.slice(startIndex, startIndex + tokenCount);

          if (span.some((token) => token.consumed)) {
            continue;
          }

          const isMatch = span.every((token, offset) => {
            return this.normalizeFuzzyTerm(token.text) === termTokens[offset];
          });

          if (!isMatch) {
            continue;
          }

          span.forEach((token) => {
            token.consumed = true;
          });

          this.addUniqueEntry(matchedEntries, entry);
          changed = true;
        }
      });

      return changed;
    }

    consumeFuzzyIntentTerms(tokens, entries, matchedEntries) {
      let changed = false;

      entries
        .filter((entry) => entry?.fuzzy_match === true)
        .forEach((entry) => {
          const fuzzyTerms = Array.isArray(entry?.fuzzy_terms)
            ? entry.fuzzy_terms
            : entry?.terms;

          const maximumWordCount = Math.max(
            1,
            ...(Array.isArray(fuzzyTerms) ? fuzzyTerms : []).map((term) => {
              return Math.max(1, this.tokenizeQuery(term).length);
            })
          );

          for (
            let wordCount = maximumWordCount;
            wordCount >= 1;
            wordCount -= 1
          ) {
            for (
              let startIndex = 0;
              startIndex <= tokens.length - wordCount;
              startIndex += 1
            ) {
              const span = tokens.slice(startIndex, startIndex + wordCount);

              if (span.some((token) => token.consumed)) {
                continue;
              }

              const phrase = span.map((token) => token.text).join(" ");

              if (!this.isFuzzyEntryMatch(entry, phrase)) {
                continue;
              }

              span.forEach((token) => {
                token.consumed = true;
              });

              this.addUniqueEntry(matchedEntries, entry);
              changed = true;
            }
          }
        });

      return changed;
    }

    getOlfactiveTermCandidates() {
      const seen = new Set();

      return this.getOlfactiveIntentEntries()
        .flatMap((entry) => {
          return entry.terms.map((term) => {
            const termTokens = this.tokenizeQuery(term)
              .map((token) => this.normalizeFuzzyTerm(token.text))
              .filter(Boolean);

            return {
              entry,
              termTokens,
              termLength: termTokens.join("").length
            };
          });
        })
        .filter((candidate) => {
          if (!candidate.termTokens.length) {
            return false;
          }

          const key = `${this.getEntryKey(candidate.entry)}\u0000${candidate.termTokens.join(" ")}`;

          if (seen.has(key)) {
            return false;
          }

          seen.add(key);
          return true;
        })
        .sort((candidateA, candidateB) => {
          return (
            candidateB.termTokens.length - candidateA.termTokens.length ||
            candidateB.termLength - candidateA.termLength ||
            Number(candidateB.entry?.priority || 0) -
              Number(candidateA.entry?.priority || 0)
          );
        });
    }

    findOlfactiveTokenMatches(tokens) {
      const occupiedIndexes = new Set();
      const matches = [];

      this.getOlfactiveTermCandidates().forEach(({ entry, termTokens }) => {
        const tokenCount = termTokens.length;

        /*
         * Et enkelt Olfactive character-ord skal selv opfylde den globale
         * minimumslængde. Ellers bliver produktnavnet "Ho Wood" fejlagtigt
         * læst som kombinationen Hø + Woody, fordi ø normaliseres til o.
         */
        if (
          tokenCount === 1 &&
          Array.from(termTokens[0] || "").length < this.minimumQueryLength
        ) {
          return;
        }

        for (
          let startIndex = 0;
          startIndex <= tokens.length - tokenCount;
          startIndex += 1
        ) {
          const indexes = Array.from(
            { length: tokenCount },
            (_, offset) => startIndex + offset
          );

          if (
            indexes.some(
              (index) => tokens[index]?.consumed || occupiedIndexes.has(index)
            )
          ) {
            continue;
          }

          const isMatch = indexes.every((index, offset) => {
            return (
              this.normalizeFuzzyTerm(tokens[index]?.text) === termTokens[offset]
            );
          });

          if (!isMatch) {
            continue;
          }

          indexes.forEach((index) => occupiedIndexes.add(index));
          matches.push({ entry, indexes });
          break;
        }
      });

      return matches.sort((matchA, matchB) => {
        return Math.min(...matchA.indexes) - Math.min(...matchB.indexes);
      });
    }

    hasExplicitOlfactiveConnector(searchTerm, tokens, matches) {
      const rawTerm = String(searchTerm || "").normalize("NFC");
      const connectorWords = new Set(
        (Array.isArray(this.getIntentLanguageConfig()?.olfactive_connectors)
          ? this.getIntentLanguageConfig().olfactive_connectors
          : []
        )
          .map((value) => this.normalizeFuzzyTerm(value))
          .filter(Boolean)
      );

      const orderedMatches = [...matches].sort((matchA, matchB) => {
        return Math.min(...matchA.indexes) - Math.min(...matchB.indexes);
      });

      for (let index = 0; index < orderedMatches.length - 1; index += 1) {
        const leftMatch = orderedMatches[index];
        const rightMatch = orderedMatches[index + 1];
        const leftTokenIndex = Math.max(...leftMatch.indexes);
        const rightTokenIndex = Math.min(...rightMatch.indexes);
        const leftToken = tokens[leftTokenIndex];
        const rightToken = tokens[rightTokenIndex];

        const tokensBetween = tokens.slice(
          leftTokenIndex + 1,
          rightTokenIndex
        );

        if (
          tokensBetween.some((token) => {
            return (
              !token.consumed &&
              connectorWords.has(this.normalizeFuzzyTerm(token.text))
            );
          })
        ) {
          return true;
        }

        if (!leftToken || !rightToken) {
          continue;
        }

        const separator = rawTerm.slice(leftToken.end, rightToken.start);

        if (/[+&,/]/u.test(separator) || /-/u.test(separator)) {
          return true;
        }
      }

      return false;
    }

    consumeOlfactiveIntentTerms(tokens, matchedEntries, searchTerm) {
      const matches = this.findOlfactiveTokenMatches(tokens);
      const uniqueOlfactiveEntries = this.getUniqueEntries(
        matches.map((match) => match.entry)
      );

      if (uniqueOlfactiveEntries.length < 2) {
        return {
          changed: false,
          entries: []
        };
      }

      const matchedIndexes = new Set(
        matches.flatMap((match) => match.indexes)
      );

      const languageConfig = this.getIntentLanguageConfig();
      const connectorWords = new Set(
        (Array.isArray(languageConfig?.olfactive_connectors)
          ? languageConfig.olfactive_connectors
          : []
        )
          .map((value) => this.normalizeFuzzyTerm(value))
          .filter(Boolean)
      );

      const contextWords = new Set(
        (Array.isArray(languageConfig?.olfactive_context_words)
          ? languageConfig.olfactive_context_words
          : []
        )
          .map((value) => this.normalizeFuzzyTerm(value))
          .filter(Boolean)
      );

      const explicitConnector = this.hasExplicitOlfactiveConnector(
        searchTerm,
        tokens,
        matches
      );

      const remainingMeaningfulTokens = tokens.filter((token, index) => {
        if (token.consumed || matchedIndexes.has(index)) {
          return false;
        }

        const comparableToken = this.normalizeFuzzyTerm(token.text);

        return (
          comparableToken &&
          !connectorWords.has(comparableToken) &&
          !contextWords.has(comparableToken)
        );
      });

      /*
       * Uden et tydeligt bindeord eller tegn må hele søgningen være en
       * duftbeskrivelse. Dermed bliver "frisk frugtagtig" en præcis
       * kombination, mens "lavendel frisk frugtagtig" ikke overfortolkes.
       * Med +, &, /, komma, bindestreg, "og" eller "and" kan et produktnavn
       * godt stå sammen med to eller flere eksplicitte duftkarakterer.
       */
      if (!explicitConnector && remainingMeaningfulTokens.length) {
        return {
          changed: false,
          entries: []
        };
      }

      matches.forEach((match) => {
        match.indexes.forEach((index) => {
          if (tokens[index]) {
            tokens[index].consumed = true;
          }
        });

        this.addUniqueEntry(matchedEntries, match.entry);
      });

      tokens.forEach((token) => {
        if (token.consumed) {
          return;
        }

        const comparableToken = this.normalizeFuzzyTerm(token.text);

        if (
          connectorWords.has(comparableToken) ||
          contextWords.has(comparableToken)
        ) {
          token.consumed = true;
        }
      });

      return {
        changed: true,
        entries: uniqueOlfactiveEntries
      };
    }

    stripGenericProductDescriptors(tokens, facetEntries) {
      const languageConfig = this.getIntentLanguageConfig();
      const genericWords = new Set(
        (Array.isArray(languageConfig?.generic_product_words)
          ? languageConfig.generic_product_words
          : []
        )
          .map((value) => this.normalizeIntentToken(value))
          .filter(Boolean)
      );

      const suffixes = (
        Array.isArray(languageConfig?.generic_product_suffixes)
          ? languageConfig.generic_product_suffixes
          : []
      )
        .map((value) => this.normalizeIntentToken(value))
        .filter(Boolean)
        .sort((suffixA, suffixB) => suffixB.length - suffixA.length);

      const exclusions = new Set(
        (Array.isArray(languageConfig?.generic_product_suffix_exclusions)
          ? languageConfig.generic_product_suffix_exclusions
          : []
        )
          .map((value) => this.normalizeIntentToken(value))
          .filter(Boolean)
      );

      let changed = false;

      let trailingTokenIndex = -1;

      for (let index = tokens.length - 1; index >= 0; index -= 1) {
        if (!tokens[index].consumed && tokens[index].text) {
          trailingTokenIndex = index;
          break;
        }
      }

      if (trailingTokenIndex >= 0) {
        const trailingToken = tokens[trailingTokenIndex];
        const comparableTrailingToken = this.normalizeIntentToken(
          trailingToken.text
        );
        const hasEarlierProductTerm = tokens
          .slice(0, trailingTokenIndex)
          .some((token) => !token.consumed && token.text);
        const isGenericWordPrefix =
          comparableTrailingToken.length >= 2 &&
          !genericWords.has(comparableTrailingToken) &&
          [...genericWords].some((word) =>
            word.startsWith(comparableTrailingToken)
          );

        if (
          isGenericWordPrefix &&
          (hasEarlierProductTerm || facetEntries.length)
        ) {
          trailingToken.consumed = true;
          changed = true;
        }
      }

      tokens.forEach((token) => {
        if (token.consumed) {
          return;
        }

        const comparableToken = this.normalizeIntentToken(token.text);

        if (
          !comparableToken ||
          genericWords.has(comparableToken) ||
          exclusions.has(comparableToken)
        ) {
          return;
        }

        const matchingSuffix = suffixes.find((suffix) => {
          return (
            comparableToken.endsWith(suffix) &&
            comparableToken.length - suffix.length >= 3
          );
        });

        if (!matchingSuffix) {
          return;
        }

        token.text = comparableToken.slice(0, -matchingSuffix.length);
        token.consumed = !token.text;
        changed = true;
      });

      const unconsumedTokens = tokens.filter(
        (token) => !token.consumed && token.text
      );

      const hasNonGenericProductTerm = unconsumedTokens.some((token) => {
        return !genericWords.has(this.normalizeIntentToken(token.text));
      });

      if (hasNonGenericProductTerm || facetEntries.length) {
        unconsumedTokens.forEach((token) => {
          if (genericWords.has(this.normalizeIntentToken(token.text))) {
            token.consumed = true;
            changed = true;
          }
        });
      }

      return changed;
    }

    rewriteRemainingProductTerms(tokens) {
      const rewrites =
        this.getIntentLanguageConfig()?.product_term_rewrites_after_generic_word ||
        {};

      let changed = false;

      tokens.forEach((token) => {
        if (token.consumed || !token.text) {
          return;
        }

        const comparableToken = this.normalizeIntentToken(token.text);
        const replacement = this.normalizeTerm(rewrites[comparableToken]);

        if (!replacement || replacement === token.text) {
          return;
        }

        token.text = replacement;
        changed = true;
      });

      return changed;
    }

    classifyFacetEntries(entries, searchTerm) {
      const allIntentEntries = this.getUniqueEntries(entries);
      const hasProductTerm = Boolean(this.normalizeTerm(searchTerm));

      /*
       * En eksplicit type efter et produktnavn er en søgekvalifikator, ikke
       * et synligt storefront-filter. "Lavendel æterisk olie" skal derfor
       * kun vise Lavendel-produkter af den rigtige type, mens Type stadig
       * står på "Alle produkter". Rene typesøgninger beholder typefilteret.
       */
      const productQualifierEntries = hasProductTerm
        ? allIntentEntries.filter((entry) => entry?.kind === "type")
        : [];

      const qualifierKeys = new Set(
        productQualifierEntries.map((entry) => this.getEntryKey(entry))
      );

      const facetEntries = allIntentEntries.filter((entry) => {
        return !qualifierKeys.has(this.getEntryKey(entry));
      });

      const preferredFacetEntries = hasProductTerm
        ? facetEntries.filter((entry) => entry?.soft_with_product_query === true)
        : [];

      const preferredKeys = new Set(
        preferredFacetEntries.map((entry) => this.getEntryKey(entry))
      );

      const requiredFacetEntries = facetEntries.filter((entry) => {
        return !preferredKeys.has(this.getEntryKey(entry));
      });

      return {
        allIntentEntries,
        facetEntries,
        requiredFacetEntries,
        preferredFacetEntries,
        productQualifierEntries
      };
    }

    analyzeQuery(searchTerm) {
      const tokens = this.tokenizeQuery(searchTerm);
      const composableEntries = this.getComposableIntentEntries();
      const facetEntries = [];

      const compoundIntentChanged = this.applyCompoundIntentSuffixes(
        tokens,
        composableEntries,
        facetEntries
      );

      const exactIntentChanged = this.consumeExactIntentTerms(
        tokens,
        composableEntries,
        facetEntries
      );

      const fuzzyIntentChanged = this.consumeFuzzyIntentTerms(
        tokens,
        composableEntries,
        facetEntries
      );

      /*
       * Fuldstændige typeudtryk skal altid vinde over en mulig delvis
       * fortolkning af deres sidste ord. Det forhindrer fx "æterisk olie"
       * i samtidig at blive læst som begyndelsen på "olie blanding".
       */
      const trailingIntentPrefixChanged =
        this.consumeTrailingProductIntentPrefixes(
          tokens,
          composableEntries,
          facetEntries
        );

      const productTermBeforeGeneric = this.getRemainingSearchTerm(tokens);

      const genericCleanupChanged = this.stripGenericProductDescriptors(
        tokens,
        facetEntries
      );

      const rewriteChanged = this.rewriteRemainingProductTerms(tokens);

      const olfactiveIntent = this.consumeOlfactiveIntentTerms(
        tokens,
        facetEntries,
        searchTerm
      );

      const remainingSearchTerm = this.getRemainingSearchTerm(tokens);
      const classification = this.classifyFacetEntries(
        facetEntries,
        remainingSearchTerm
      );

      const searchTermVariants = [
        remainingSearchTerm,
        productTermBeforeGeneric
      ]
        .map((value) => this.normalizeTerm(value))
        .filter(Boolean)
        .filter((value, index, values) => values.indexOf(value) === index);

      return {
        originalTerm: String(searchTerm || "").trim(),
        searchTerm: remainingSearchTerm,
        searchTermVariants,
        ...classification,
        hasComposableIntent: classification.allIntentEntries.length > 0,
        isIntentOnly:
          classification.allIntentEntries.length > 0 && !remainingSearchTerm,
        hasProductCleanup:
          genericCleanupChanged ||
          rewriteChanged ||
          classification.productQualifierEntries.length > 0,
        hasOlfactiveCombination: olfactiveIntent.entries.length >= 2,
        intentWasConsumed:
          compoundIntentChanged ||
          trailingIntentPrefixChanged ||
          exactIntentChanged ||
          fuzzyIntentChanged ||
          olfactiveIntent.changed
      };
    }

    getDamerauLevenshteinDistance(sourceValue, targetValue) {
      const source = Array.from(String(sourceValue || ""));
      const target = Array.from(String(targetValue || ""));

      if (!source.length) {
        return target.length;
      }

      if (!target.length) {
        return source.length;
      }

      const matrix = Array.from(
        { length: source.length + 1 },
        () => new Array(target.length + 1).fill(0)
      );

      for (let sourceIndex = 0; sourceIndex <= source.length; sourceIndex += 1) {
        matrix[sourceIndex][0] = sourceIndex;
      }

      for (let targetIndex = 0; targetIndex <= target.length; targetIndex += 1) {
        matrix[0][targetIndex] = targetIndex;
      }

      for (let sourceIndex = 1; sourceIndex <= source.length; sourceIndex += 1) {
        for (let targetIndex = 1; targetIndex <= target.length; targetIndex += 1) {
          const substitutionCost =
            source[sourceIndex - 1] === target[targetIndex - 1] ? 0 : 1;

          matrix[sourceIndex][targetIndex] = Math.min(
            matrix[sourceIndex - 1][targetIndex] + 1,
            matrix[sourceIndex][targetIndex - 1] + 1,
            matrix[sourceIndex - 1][targetIndex - 1] + substitutionCost
          );

          const isAdjacentTransposition =
            sourceIndex > 1 &&
            targetIndex > 1 &&
            source[sourceIndex - 1] === target[targetIndex - 2] &&
            source[sourceIndex - 2] === target[targetIndex - 1];

          if (isAdjacentTransposition) {
            matrix[sourceIndex][targetIndex] = Math.min(
              matrix[sourceIndex][targetIndex],
              matrix[sourceIndex - 2][targetIndex - 2] + 1
            );
          }
        }
      }

      return matrix[source.length][target.length];
    }

    isFuzzyEntryMatch(entry, searchTerm) {
      if (!entry?.fuzzy_match) {
        return false;
      }

      const normalizedSearchTerm = this.normalizeFuzzyTerm(searchTerm);
      const minimumLength = Math.max(
        1,
        Number(entry.fuzzy_min_length || 5)
      );

      if (normalizedSearchTerm.length < minimumLength) {
        return false;
      }

      const maximumDistance = Math.max(
        0,
        Number(entry.max_edit_distance || 1)
      );

      const fuzzyTerms = Array.isArray(entry.fuzzy_terms)
        ? entry.fuzzy_terms
        : entry.terms;

      if (!Array.isArray(fuzzyTerms)) {
        return false;
      }

      const fuzzyExclusions = Array.isArray(entry.fuzzy_exclusions)
        ? entry.fuzzy_exclusions
            .map((term) => this.normalizeFuzzyTerm(term))
            .filter(Boolean)
        : [];

      if (fuzzyExclusions.includes(normalizedSearchTerm)) {
        return false;
      }

      return fuzzyTerms.some((term) => {
        const normalizedTerm = this.normalizeFuzzyTerm(term);

        if (!normalizedTerm) {
          return false;
        }

        if (normalizedTerm === normalizedSearchTerm) {
          return true;
        }

        /*
         * Et afkortet ord er et præfiksmatch, ikke en stavefejl.
         * Det er vigtigt for fx "organ", som ikke i sig selv er en
         * tydelig søgning efter økologiske produkter.
         */
        if (normalizedTerm.startsWith(normalizedSearchTerm)) {
          return false;
        }

        if (
          Math.abs(normalizedTerm.length - normalizedSearchTerm.length) >
          maximumDistance
        ) {
          return false;
        }

        return (
          this.getDamerauLevenshteinDistance(
            normalizedSearchTerm,
            normalizedTerm
          ) <= maximumDistance
        );
      });
    }


    handleizePathTag(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/æ/g, "ae")
        .replace(/ø/g, "o")
        .replace(/å/g, "a")
        .replace(/Æ/g, "ae")
        .replace(/Ø/g, "o")
        .replace(/Å/g, "a")
        .toLocaleLowerCase("en-US")
        .trim()
        .replace(/&/g, "-and-")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    getSearchRoute() {
      return (
        this.form?.getAttribute("action") ||
        this.config.searchUrl ||
        (typeof routes !== "undefined" &&
          routes.search_url) ||
        `${window.Shopify?.routes?.root || "/"}search`
      );
    }

    buildFacetCollectionUrl(
      entries,
      { sectionId = null, sortBy = null } = {}
    ) {
      const allProductsUrl =
        this.searchMap?.all_products_url ||
        `${window.Shopify?.routes?.root || "/"}collections/all`;

      const url = new URL(
        allProductsUrl,
        window.location.origin
      );

      const uniqueEntries =
        this.getUniqueEntries(entries);

      const tagHandles = [
        ...new Set(
          uniqueEntries
            .filter(
              (entry) =>
                entry?.filter_param ===
                FILTER_PARAM_NAMES.olfactive
            )
            .map((entry) =>
              this.handleizePathTag(
                entry?.filter_value
              )
            )
            .filter(Boolean)
        )
      ];

      url.searchParams.delete(
        FILTER_PARAM_NAMES.olfactive
      );

      uniqueEntries
        .filter(
          (entry) =>
            entry?.filter_param !==
            FILTER_PARAM_NAMES.olfactive
        )
        .forEach((entry) => {
          this.applyFacetEntryToParams(
            url.searchParams,
            entry
          );
        });

      if (tagHandles.length) {
        url.pathname =
          `${url.pathname.replace(/\/$/, "")}/` +
          tagHandles.join("+");
      }

      if (sortBy) {
        url.searchParams.set(
          "sort_by",
          sortBy
        );
      }

      if (sectionId) {
        url.searchParams.set(
          "section_id",
          sectionId
        );
      }

      return url.toString();
    }

    isQueryLongEnough(value) {
      return (
        this.normalizeTerm(value).length >=
        this.minimumQueryLength
      );
    }

    bindForm() {
      if (!this.form || !this.input) {
        return;
      }

      /*
       * Rene og entydigt påbegyndte typesøgninger går direkte til Alle
       * produkter med Type valgt. Når der står et egentligt produktord
       * tilbage, bevares /search-oplevelsen, så titelmatch, synonymer og
       * den bløde Økologisk-fallback fortsat kan arbejde.
       */
      this.form.addEventListener("submit", async (event) => {
        const query = this.input.value.trim();

        if (!this.isQueryLongEnough(query)) {
          return;
        }

        event.preventDefault();

        window.EsenseraSearchHistory?.save?.(query);
        this.setLoading(true);

        const destination =
          await this.resolveSearchSubmissionUrl(query);

        window.location.assign(destination);
      });
    }

    isPureTypeIntent(queryContext) {
      const entries = Array.isArray(
        queryContext?.entries
      )
        ? queryContext.entries
        : [];

      return (
        queryContext?.analysis?.isIntentOnly === true &&
        entries.length === 1 &&
        entries[0]?.kind === "type"
      );
    }

    hasStrictOrganicTypeIntent(queryContext) {
      if (!queryContext?.analysis?.isIntentOnly) {
        return false;
      }

      const hasOrganic = queryContext.entries.some(
        (entry) => entry?.kind === "organic"
      );

      const hasType = queryContext.entries.some(
        (entry) => entry?.kind === "type"
      );

      return hasOrganic && hasType;
    }

    async fetchStrictIntentProductCount(queryContext) {
      const requestUrl = this.buildFacetCollectionUrl(
        queryContext.entries,
        { sectionId: this.sectionId }
      );

      const controller = new AbortController();
      const timeout = window.setTimeout(
        () => controller.abort(),
        3000
      );

      try {
        const sourcePage = await this.fetchSourcePage(
          requestUrl,
          controller.signal
        );

        const strictParams = new URLSearchParams();

        queryContext.entries.forEach((entry) => {
          this.applyFacetEntryToParams(
            strictParams,
            entry
          );
        });

        return this.filterProducts(
          sourcePage.products,
          strictParams
        ).length;
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn(
            "Den strikse søgeintention kunne ikke kontrolleres:",
            error
          );
        }

        return null;
      } finally {
        window.clearTimeout(timeout);
      }
    }

    async resolveSearchSubmissionUrl(query) {
      const directDestinationEntry =
        this.getDirectDestinationEntry(query);

      if (directDestinationEntry) {
        return directDestinationEntry.url;
      }

      const directCollectionEntry =
        this.getExactCollectionDestinationEntry(query);

      if (directCollectionEntry) {
        return directCollectionEntry.url;
      }

      const exactFacetEntry =
        this.getExactQueryFacetEntry(query);

      if (exactFacetEntry) {
        return exactFacetEntry.url;
      }

      return this.buildSearchResultsSubmissionUrl(query);
    }

    buildSearchSubmissionUrl(query) {
      const directDestinationEntry =
        this.getDirectDestinationEntry(query);

      if (directDestinationEntry) {
        return directDestinationEntry.url;
      }

      const directCollectionEntry =
        this.getExactCollectionDestinationEntry(query);

      if (directCollectionEntry) {
        return directCollectionEntry.url;
      }

      const exactFacetEntry =
        this.getExactQueryFacetEntry(query);

      if (exactFacetEntry) {
        return exactFacetEntry.url;
      }

      return this.buildSearchResultsSubmissionUrl(query);
    }

    buildSearchResultsSubmissionUrl(query) {
      const url = new URL(
        this.getSearchRoute(),
        window.location.origin
      );

      const formData = new FormData(this.form);

      formData.forEach((value, key) => {
        if (key === "q" || key.startsWith("filter.")) {
          return;
        }

        url.searchParams.append(key, String(value));
      });

      url.searchParams.set("q", query);

      const searchParams = url.searchParams.toString();

      return searchParams
        ? `${url.pathname}?${searchParams}`
        : url.pathname;
    }

    clearSearchExperience() {
      /*
       * Ryd søgningen uden en fuld sidegenindlæsning. Dermed kan
       * fokus og mobilens tastatur bevares, mens den tomme søgeside
       * med populære og tidligere søgninger vises med det samme.
       */
      this.abortController?.abort();
      ++this.collectionContextRequestId;
      this.collectionContextAbortController?.abort();
      this.collectionContextAbortController = null;

      this.activeFacetRefreshTimers.forEach(
        (timer) => window.clearTimeout(timer)
      );
      this.activeFacetRefreshTimers = [];

      this.query = "";
      this.pendingFacetSearchParams = null;
      this.currentSearchParams =
        new URLSearchParams();

      if (this.input) {
        this.input.value = "";

        try {
          this.input.focus({
            preventScroll: true
          });
        } catch (error) {
          this.input.focus();
        }
      }

      this.form
        ?.querySelector(".clear-btn")
        ?.classList.add("d-none");

      const minimumMessage = this.root.querySelector(
        "#EsenseraSearchMinimumMessage"
      );

      if (minimumMessage) {
        minimumMessage.hidden = true;
      }

      this.input?.removeAttribute("aria-invalid");

      if (this.resultsStateContainer) {
        this.resultsStateContainer.hidden = true;
      }

      if (this.emptyStateContainer) {
        this.emptyStateContainer.hidden = false;
      }

      if (this.collectionContextContainer) {
        this.collectionContextContainer.hidden = true;
        this.collectionContextContainer.removeAttribute(
          "aria-busy"
        );
        this.collectionContextContainer.replaceChildren();
      }

      this.root.classList.add(
        "template-search--empty"
      );
      this.root.setAttribute(
        "data-esensera-search-too-short",
        "false"
      );
      this.root.removeAttribute(
        "data-esensera-custom-search-pending"
      );
      this.root.removeAttribute(
        "data-esensera-custom-search-ready"
      );
      this.root.removeAttribute(
        "data-esensera-custom-facets-ready"
      );
      this.root.removeAttribute(
        "data-esensera-has-collection-context"
      );
      this.root.removeAttribute(
        "data-esensera-no-product-results"
      );
      this.root.setAttribute(
        "data-esensera-search-has-query",
        "false"
      );

      this.setLoading(false);

      const title = this.root.querySelector(
        ".page-title h1"
      );

      if (title) {
        title.textContent =
          this.config.emptyTitleText ||
          (this.isDanish()
            ? "Søg"
            : "Search");
      }

      if (this.config.emptyDocumentTitle) {
        document.title =
          this.config.emptyDocumentTitle;
      }

      const searchRoute =
        this.config.searchUrl ||
        `${window.Shopify?.routes?.root || "/"}search`;

      const cleanUrl = new URL(
        searchRoute,
        window.location.origin
      );

      window.history.replaceState(
        {
          searchParams: ""
        },
        "",
        cleanUrl.pathname
      );

      window.EsenseraSearchHistory?.render?.();

      /*
       * En ekstra fokusmarkering efter DOM-opdateringen hjælper
       * især Safari, uden at den direkte fokus fra klik-eventet mistes.
       */
      window.setTimeout(() => {
        if (!this.input) {
          return;
        }

        try {
          this.input.focus({
            preventScroll: true
          });
        } catch (error) {
          this.input.focus();
        }
      }, 0);

      return true;
    }

    bindCustomFacetLinks() {
      this.root.addEventListener("click", (event) => {
        const link = event.target.closest(
          "[data-esensera-custom-facet-url]"
        );

        if (!link) {
          return;
        }

        event.preventDefault();

        const url = new URL(
          link.getAttribute("href") || window.location.href,
          window.location.origin
        );

        this.applyFacetSearchParams(
          url.searchParams.toString(),
          {
            updateHistory: true
          }
        );
      });
    }

    getFacetEntryByValue(paramName, value) {
      const normalizedValue = this.normalizeTerm(value);

      if (!paramName || !normalizedValue || !Array.isArray(this.searchMap?.entries)) {
        return null;
      }

      return (
        this.searchMap.entries
          .filter((entry) => {
            return (
              entry?.filter_param === paramName &&
              this.normalizeTerm(entry?.filter_value) === normalizedValue &&
              Boolean(entry?.url)
            );
          })
          .sort((entryA, entryB) => {
            return Number(entryB?.priority || 0) - Number(entryA?.priority || 0);
          })[0] || null
      );
    }

    buildBrowseFacetDestination(input) {
      const paramName = String(input?.name || "").trim();
      const value = String(input?.value || "").trim();

      if (!paramName || !value) {
        return null;
      }

      const allProductsUrl =
        this.searchMap?.all_products_url ||
        `${window.Shopify?.routes?.root || "/"}collections/all`;

      if (paramName === FILTER_PARAM_NAMES.type) {
        const typeHandle =
          input.closest(".js-type-option[data-type-handle]")?.dataset.typeHandle || "";

        if (typeHandle === "all" || this.normalizeTerm(value) === "all") {
          return new URL(allProductsUrl, window.location.origin).toString();
        }

        const typeEntry = this.getFacetEntryByValue(paramName, value);

        if (typeEntry?.url) {
          return new URL(typeEntry.url, window.location.origin).toString();
        }
      }

      const url = new URL(allProductsUrl, window.location.origin);

      /*
       * Olfactive character bruger collection-tag-paths på katalogsiden,
       * så den eksisterende AND-stack-logik kan tage over efter navigation.
       * Search-map-URL'en bruges derfor ikke til netop denne brugerhandling.
       */
      if (paramName === FILTER_PARAM_NAMES.olfactive) {
        const tagHandle = this.handleizePathTag(value);

        if (!tagHandle) {
          return null;
        }

        url.pathname = `${url.pathname.replace(/\/$/, "")}/${tagHandle}`;
        return url.toString();
      }

      const entry = this.getFacetEntryByValue(paramName, value);

      if (entry?.url) {
        return new URL(entry.url, window.location.origin).toString();
      }

      const browseFacetParams = new Set([
        FILTER_PARAM_NAMES.scentProfile,
        FILTER_PARAM_NAMES.note,
        FILTER_PARAM_NAMES.aromatherapy,
        FILTER_PARAM_NAMES.organic
      ]);

      if (!browseFacetParams.has(paramName)) {
        return null;
      }

      url.searchParams.set(paramName, value);
      return url.toString();
    }

    bindBrowseFacetNavigation() {
      const browseFacetNames = new Set([
        FILTER_PARAM_NAMES.type,
        FILTER_PARAM_NAMES.scentProfile,
        FILTER_PARAM_NAMES.note,
        FILTER_PARAM_NAMES.aromatherapy,
        FILTER_PARAM_NAMES.organic,
        FILTER_PARAM_NAMES.olfactive
      ]);

      /*
       * På /search er facetter navigation i materialebiblioteket, ikke en
       * ekstra begrænsning af den frie søgning. Et klik forlader derfor
       * q-konteksten helt og går til den valgte katalogdestination.
       * Sortering er ikke en facet og forbliver lokal på søgeresultatet.
       */
      this.root.addEventListener(
        "input",
        (event) => {
          const input = event.target;

          if (
            !(input instanceof HTMLInputElement) ||
            input.type !== "checkbox" ||
            input.disabled ||
            !browseFacetNames.has(input.name)
          ) {
            return;
          }

          const destination = this.buildBrowseFacetDestination(input);

          if (!destination) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          this.setLoading(true);
          window.location.assign(destination);
        },
        true
      );
    }

    ensureActiveFacetPersistenceStyles() {
      if (
        document.getElementById(
          "esensera-active-facet-persistence"
        )
      ) {
        return;
      }

      const style =
        document.createElement("style");

      style.id =
        "esensera-active-facet-persistence";

      style.textContent = `
        .template-search
        [data-esensera-active-facets][data-esensera-query-active="true"] {
          display: flex !important;
          visibility: visible !important;
        }

        /*
         * Den aktive søgning ligner fortsat temaets øvrige filterchips,
         * men får kun ét diskret vægttrin mere end de øvrige chips.
         */
        .template-search [data-esensera-search-query-chip] {
          font-weight: 600 !important;
          /*
           * Konturen skal ikke følge tekstfarven. På hover bliver teksten
           * hvid, og currentColor gav derfor en lys ring/halo rundt om den
           * sorte chip. En fast heading-farve bevarer den mørke kontur i
           * hvile og lader den falde visuelt sammen med hover-baggrunden.
           */
          box-shadow: inset 0 0 0 1px var(--heading-color);
        }
      `;

      document.head.appendChild(style);
    }

    scheduleActiveFacetRefresh(params) {
      this.activeFacetRefreshTimers.forEach(
        (timer) => window.clearTimeout(timer)
      );

      this.activeFacetRefreshTimers = [];

      const paramsString =
        new URLSearchParams(params).toString();

      /*
       * Temaets facetkode kan afslutte sit eget DOM-arbejde efter den
       * lokale søge-controller. Kør derfor en kort og en forsinket
       * genoptegning, så den frie søgning ikke får Shopifys gamle
       * server-renderede facet-/"Fjern alle"-markup tilbage.
       */
      [0, 650].forEach((delay) => {
        const timer = window.setTimeout(() => {
          if (!this.root?.isConnected) {
            return;
          }

          this.updateActiveFacets(
            new URLSearchParams(paramsString)
          );
        }, delay);

        this.activeFacetRefreshTimers.push(timer);
      });
    }

    getGenericProductWords() {
      return new Set(
        (Array.isArray(
          this.getIntentLanguageConfig()?.generic_product_words
        )
          ? this.getIntentLanguageConfig().generic_product_words
          : []
        )
          .map((value) => this.normalizeFuzzyTerm(value))
          .filter(Boolean)
      );
    }

    isAllowedSearchMapPrefix(
      entry,
      normalizedSearchTerm,
      normalizedTerm
    ) {
      if (
        !normalizedTerm.startsWith(normalizedSearchTerm) ||
        normalizedTerm.length <= normalizedSearchTerm.length
      ) {
        return false;
      }

      const configuredMinimum = Math.max(
        this.minimumQueryLength,
        Number(entry?.prefix_min_length || 0)
      );

      if (normalizedSearchTerm.length < configuredMinimum) {
        return false;
      }

      if (
        this.getGenericProductWords().has(
          this.normalizeFuzzyTerm(normalizedSearchTerm)
        )
      ) {
        return false;
      }

      const prefixExclusions = Array.isArray(entry?.prefix_exclusions)
        ? entry.prefix_exclusions
            .map((term) => this.normalizeFuzzyTerm(term))
            .filter(Boolean)
        : [];

      if (prefixExclusions.includes(normalizedSearchTerm)) {
        return false;
      }

      return true;
    }

    getSearchMapPrefixDetails(entry, searchTerm) {
      const normalizedSearchTerm =
        this.normalizeFuzzyTerm(searchTerm);

      if (
        !normalizedSearchTerm ||
        !Array.isArray(entry?.terms)
      ) {
        return null;
      }

      const prefixExclusions = Array.isArray(entry?.prefix_exclusions)
        ? entry.prefix_exclusions
            .map((term) => this.normalizeFuzzyTerm(term))
            .filter(Boolean)
        : [];

      if (prefixExclusions.includes(normalizedSearchTerm)) {
        return null;
      }

      const canonicalValues = [
        entry?.label,
        entry?.collection_label,
        entry?.filter_value,
        entry?.handle
      ]
        .map((value) => this.normalizeFuzzyTerm(value))
        .filter(Boolean);

      let bestMatch = null;

      entry.terms.forEach((term) => {
        const normalizedTerm = this.normalizeFuzzyTerm(term);

        if (
          !normalizedTerm ||
          !this.isAllowedSearchMapPrefix(
            entry,
            normalizedSearchTerm,
            normalizedTerm
          )
        ) {
          return;
        }

        const termWordCount = normalizedTerm
          .split(" ")
          .filter(Boolean).length;

        const remainingLength = Math.max(
          0,
          normalizedTerm.length - normalizedSearchTerm.length
        );

        const canonicalPrefix = canonicalValues.some((value) => {
          return value.startsWith(normalizedSearchTerm);
        });

        const score =
          (canonicalPrefix ? 100000 : 0) +
          (termWordCount === 1 ? 10000 : 0) +
          Math.max(0, 1000 - remainingLength);

        if (!bestMatch || score > bestMatch.score) {
          bestMatch = {
            score,
            term: normalizedTerm
          };
        }
      });

      return bestMatch;
    }

    filterAmbiguousSearchMapPrefixMatches(matchRecords) {
      const protectedKinds = new Set([
        "type",
        "scent_profile",
        "note",
        "aromatherapy",
        "organic",
        "olfactive_character"
      ]);

      const strictUniqueKinds = new Set([
        "note",
        "aromatherapy",
        "organic"
      ]);

      const exactKinds = new Set(
        matchRecords
          .filter((match) => match.matchType === "exact")
          .map((match) => match.entry?.kind)
          .filter(Boolean)
      );

      const prefixGroups = new Map();

      matchRecords.forEach((match) => {
        const kind = match.entry?.kind;

        if (
          match.matchType !== "prefix" ||
          !protectedKinds.has(kind) ||
          exactKinds.has(kind)
        ) {
          return;
        }

        if (!prefixGroups.has(kind)) {
          prefixGroups.set(kind, []);
        }

        prefixGroups.get(kind).push(match);
      });

      const allowedPrefixKeys = new Set();

      prefixGroups.forEach((matches, kind) => {
        /*
         * Note/Aromaterapi/Økologisk indeholder mange naturlige sprogtermer
         * med fælles begyndelser. De må først aktiveres som præfiksintention,
         * når præfikset peger på præcis én entry.
         */
        if (strictUniqueKinds.has(kind) && matches.length !== 1) {
          return;
        }

        const sortedMatches = [...matches].sort((matchA, matchB) => {
          return (
            Number(matchB.prefixScore || 0) -
              Number(matchA.prefixScore || 0) ||
            Number(matchB.entry?.priority || 0) -
              Number(matchA.entry?.priority || 0)
          );
        });

        const bestMatch = sortedMatches[0];
        const secondMatch = sortedMatches[1];

        if (
          !bestMatch ||
          (secondMatch &&
            Number(secondMatch.prefixScore || 0) ===
              Number(bestMatch.prefixScore || 0))
        ) {
          return;
        }

        allowedPrefixKeys.add(this.getEntryKey(bestMatch.entry));
      });

      return matchRecords.filter((match) => {
        const kind = match.entry?.kind;

        if (
          match.matchType !== "prefix" ||
          !protectedKinds.has(kind)
        ) {
          return true;
        }

        if (exactKinds.has(kind)) {
          return false;
        }

        return allowedPrefixKeys.has(this.getEntryKey(match.entry));
      });
    }

    getStructuredPrefixCandidateEntries(searchTerm) {
      const normalizedSearchTerm = this.normalizeFuzzyTerm(searchTerm);

      if (
        normalizedSearchTerm.length < this.minimumQueryLength ||
        !Array.isArray(this.searchMap?.entries)
      ) {
        return [];
      }

      const structuredKinds = new Set([
        "type",
        "scent_profile",
        "note",
        "aromatherapy",
        "organic",
        "olfactive_character"
      ]);

      return this.searchMap.entries.filter((entry) => {
        if (
          !structuredKinds.has(entry?.kind) ||
          !Array.isArray(entry?.terms) ||
          entry.allow_prefix === false ||
          (entry.match_mode === "exact_only" &&
            entry.allow_predictive_prefix !== true)
        ) {
          return false;
        }

        const normalizedTerms = entry.terms
          .map((term) => this.normalizeFuzzyTerm(term))
          .filter(Boolean);

        if (
          normalizedTerms.includes(normalizedSearchTerm) ||
          !normalizedTerms.some((term) =>
            term.startsWith(normalizedSearchTerm)
          )
        ) {
          return false;
        }

        const prefixExclusions = Array.isArray(entry?.prefix_exclusions)
          ? entry.prefix_exclusions
              .map((term) => this.normalizeFuzzyTerm(term))
              .filter(Boolean)
          : [];

        if (prefixExclusions.includes(normalizedSearchTerm)) {
          return true;
        }

        return Boolean(
          this.getSearchMapPrefixDetails(entry, searchTerm)
        );
      });
    }

    shouldAllowProductPrefixMatches(searchTerm) {
      const candidates =
        this.getStructuredPrefixCandidateEntries(searchTerm);

      if (!candidates.length) {
        return true;
      }

      const candidateKeys = new Set(
        candidates.map((entry) => this.getEntryKey(entry))
      );

      const resolvedPrefixEntries = this.getSearchMapMatches(
        searchTerm,
        { allowPrefix: true }
      ).filter((entry) =>
        candidateKeys.has(this.getEntryKey(entry))
      );

      return resolvedPrefixEntries.length > 0;
    }

    getSearchMapMatches(
      searchTerm,
      { allowPrefix = false } = {}
    ) {
      if (!Array.isArray(this.searchMap?.entries)) {
        return [];
      }

      const normalizedSearchTerm =
        this.normalizeTerm(searchTerm);

      if (!normalizedSearchTerm) {
        return [];
      }

      const matchRecords = this.searchMap.entries
        .map((entry) => {
          if (
            ![
              "exact",
              "exact_only",
              "predictive_intent"
            ].includes(entry.match_mode) ||
            !Array.isArray(entry.terms)
          ) {
            return null;
          }

          const normalizedTerms = entry.terms
            .map((term) => this.normalizeTerm(term))
            .filter(Boolean);

          const exactMatch = normalizedTerms.some(
            (term) => term === normalizedSearchTerm
          );

          const mayUsePrefix =
            allowPrefix &&
            entry.allow_prefix !== false &&
            (entry.match_mode !== "exact_only" ||
              entry.allow_predictive_prefix === true);

          const prefixDetails =
            !exactMatch && mayUsePrefix
              ? this.getSearchMapPrefixDetails(
                  entry,
                  searchTerm
                )
              : null;

          const prefixMatch = Boolean(prefixDetails);

          const fuzzyMatch =
            !exactMatch &&
            !prefixMatch &&
            this.isFuzzyEntryMatch(
              entry,
              searchTerm
            );

          if (
            !exactMatch &&
            !prefixMatch &&
            !fuzzyMatch
          ) {
            return null;
          }

          return {
            entry,
            matchRank: exactMatch
              ? 3
              : prefixMatch
                ? 2
                : 1,
            matchType: exactMatch
              ? "exact"
              : prefixMatch
                ? "prefix"
                : "fuzzy",
            prefixScore: prefixDetails?.score || 0
          };
        })
        .filter(Boolean);

      return this.filterAmbiguousSearchMapPrefixMatches(matchRecords)
        .sort((matchA, matchB) => {
          const rankDifference =
            matchB.matchRank - matchA.matchRank;

          if (rankDifference !== 0) {
            return rankDifference;
          }

          const prefixScoreDifference =
            Number(matchB.prefixScore || 0) -
            Number(matchA.prefixScore || 0);

          if (prefixScoreDifference !== 0) {
            return prefixScoreDifference;
          }

          return (
            Number(matchB.entry.priority || 0) -
            Number(matchA.entry.priority || 0)
          );
        })
        .map((match) => match.entry);
    }

    getDirectDestinationEntry(searchTerm) {
      if (!Array.isArray(this.searchMap?.entries)) {
        return null;
      }

      return (
        this.searchMap.entries
          .filter((entry) => {
            return (
              entry?.direct_destination === true &&
              Boolean(entry?.url) &&
              this.isExactEntryTermMatch(entry, searchTerm)
            );
          })
          .sort((entryA, entryB) => {
            return (
              Number(entryB?.priority || 0) -
              Number(entryA?.priority || 0)
            );
          })[0] || null
      );
    }

    isExactEntryTermMatch(entry, searchTerm) {
      const normalizedSearchTerm =
        this.normalizeTerm(searchTerm);

      const submitTerms = Array.isArray(entry?.submit_terms)
        ? entry.submit_terms
        : entry?.terms;

      if (
        !normalizedSearchTerm ||
        !Array.isArray(submitTerms)
      ) {
        return false;
      }

      return submitTerms.some((term) => {
        return (
          this.normalizeTerm(term) ===
          normalizedSearchTerm
        );
      });
    }

    shouldPreserveProductQuery(entry, searchTerm) {
      const normalizedSearchTerm = this.normalizeTerm(searchTerm);
      const priorityTerms = Array.isArray(
        entry?.product_title_priority_terms
      )
        ? entry.product_title_priority_terms
        : [];

      return priorityTerms.some((term) => {
        return this.normalizeTerm(term) === normalizedSearchTerm;
      });
    }

    getExactCollectionDestinationEntry(searchTerm) {
      if (!Array.isArray(this.searchMap?.entries)) {
        return null;
      }

      return (
        this.searchMap.entries
          .filter((entry) => {
            return (
              entry?.direct_collection_destination === true &&
              Boolean(entry?.url) &&
              !this.shouldPreserveProductQuery(entry, searchTerm) &&
              this.isExactEntryTermMatch(
                entry,
                searchTerm
              )
            );
          })
          .sort((entryA, entryB) => {
            return (
              Number(entryB?.priority || 0) -
              Number(entryA?.priority || 0)
            );
          })[0] || null
      );
    }

    getExactQueryFacetEntry(searchTerm) {
      if (!Array.isArray(this.searchMap?.entries)) {
        return null;
      }

      return (
        this.searchMap.entries
          .filter((entry) => {
            return (
              !this.shouldPreserveProductQuery(entry, searchTerm) &&
              AUTO_SELECT_FACET_KINDS.has(entry?.kind) &&
              Boolean(entry?.filter_param) &&
              String(entry?.filter_value || "").trim() !== "" &&
              this.isExactEntryTermMatch(entry, searchTerm)
            );
          })
          .sort((entryA, entryB) => {
            return (
              Number(entryB?.priority || 0) -
              Number(entryA?.priority || 0)
            );
          })[0] || null
      );
    }

    getPredictiveTypeFacetEntry(searchTerm) {
      const matches = this.getUniqueEntries(
        this.getSearchMapMatches(searchTerm, {
          allowPrefix: true
        }).filter((entry) => {
          return (
            entry?.kind === "type" &&
            Boolean(entry?.filter_param) &&
            String(entry?.filter_value || "").trim() !== ""
          );
        })
      );

      return matches.length === 1 ? matches[0] : null;
    }

    /*
     * Kun search-map entries med en eksplicit liste må forbruge en
     * ufærdig søgestreng som et struktureret filter. Dermed bliver
     * fx "ånde" entydigt, mens det bredere ord "luft" forbliver
     * fritekst og stadig kan matche produktbeskrivelser.
     */
    getConfiguredPredictiveFacetEntry(searchTerm) {
      const normalizedSearchTerm = this.normalizeTerm(searchTerm);

      if (
        !normalizedSearchTerm ||
        !Array.isArray(this.searchMap?.entries)
      ) {
        return null;
      }

      const matches = this.getUniqueEntries(
        this.searchMap.entries.filter((entry) => {
          if (
            !AUTO_SELECT_FACET_KINDS.has(entry?.kind) ||
            !Boolean(entry?.filter_param) ||
            String(entry?.filter_value || "").trim() === "" ||
            !Array.isArray(entry?.auto_select_prefix_terms)
          ) {
            return false;
          }

          const minimumLength = Math.max(
            this.minimumQueryLength,
            Number(entry?.auto_select_prefix_min_length || 0)
          );

          if (normalizedSearchTerm.length < minimumLength) {
            return false;
          }

          return entry.auto_select_prefix_terms.some((term) => {
            return this.isAllowedSearchMapPrefix(
              entry,
              normalizedSearchTerm,
              this.normalizeTerm(term)
            );
          });
        })
      );

      return matches.length === 1 ? matches[0] : null;
    }

    getPredictiveFacetEntry(searchTerm) {
      const matches = this.getUniqueEntries([
        this.getPredictiveTypeFacetEntry(searchTerm),
        this.getConfiguredPredictiveFacetEntry(searchTerm)
      ].filter(Boolean));

      return matches.length === 1 ? matches[0] : null;
    }

    getQueryFacetContext(searchTerm) {
      let analysis = this.analyzeQuery(
        searchTerm
      );

      let entries = [
        ...analysis.facetEntries
      ];

      /*
       * Kun et fuldt, eksakt search-map-term må klassificeres som den
       * strukturerede fallback. Predictive præfikser er kun forslag.
       */
      if (!entries.length) {
        const exactFacetEntry =
          this.getExactQueryFacetEntry(searchTerm);

        if (exactFacetEntry) {
          entries = [exactFacetEntry];

          const classification =
            this.classifyFacetEntries(entries, "");

          analysis = {
            ...analysis,
            searchTerm: "",
            searchTermVariants: [],
            ...classification,
            hasComposableIntent: true,
            isIntentOnly: true,
            intentWasConsumed: true
          };
        }
      }

      return {
        analysis,
        entries: this.getUniqueEntries(
          entries
        )
      };
    }
    applyFacetEntryToParams(
      params,
      entry
    ) {
      const paramName = String(
        entry?.filter_param || ""
      ).trim();

      const filterValue = String(
        entry?.filter_value || ""
      ).trim();

      if (!paramName || !filterValue) {
        return false;
      }

      const currentValues =
        params.getAll(paramName);

      const hasMatchingValue =
        currentValues.some((value) => {
          return (
            this.normalizeTerm(value) ===
            this.normalizeTerm(filterValue)
          );
        });

      const isSingleValueFacet =
        paramName ===
          FILTER_PARAM_NAMES.type ||
        paramName ===
          FILTER_PARAM_NAMES.scentProfile ||
        paramName ===
          FILTER_PARAM_NAMES.aromatherapy ||
        paramName ===
          FILTER_PARAM_NAMES.organic;

      if (isSingleValueFacet) {
        if (
          currentValues.length === 1 &&
          hasMatchingValue
        ) {
          return false;
        }

        params.delete(paramName);
        params.set(
          paramName,
          filterValue
        );

        return true;
      }

      if (hasMatchingValue) {
        return false;
      }

      params.append(
        paramName,
        filterValue
      );

      return true;
    }

    applyExactQueryFacetDefault(
      paramsInput,
      searchTerm = this.query
    ) {
      const params =
        paramsInput instanceof URLSearchParams
          ? new URLSearchParams(paramsInput)
          : new URLSearchParams(
              String(
                paramsInput || ""
              ).replace(/^\?/, "")
            );

      const entry =
        this.getExactQueryFacetEntry(
          searchTerm
        );

      const changed = entry
        ? this.applyFacetEntryToParams(
            params,
            entry
          )
        : false;

      if (changed) {
        params.delete("page");
        params.delete("section_id");
      }

      return {
        params,
        entry: entry || null,
        entries: entry ? [entry] : [],
        analysis: this.analyzeQuery(
          searchTerm
        ),
        changed
      };
    }

    applyQueryFacetDefaults(
      paramsInput,
      searchTerm = this.query,
      context = null,
      { includePreferred = true } = {}
    ) {
      const params =
        paramsInput instanceof URLSearchParams
          ? new URLSearchParams(paramsInput)
          : new URLSearchParams(
              String(
                paramsInput || ""
              ).replace(/^\?/, "")
            );

      const queryContext =
        context ||
        this.getQueryFacetContext(
          searchTerm
        );

      const entries = includePreferred
        ? queryContext.entries
        : queryContext.analysis.requiredFacetEntries;

      let changed = false;

      entries.forEach(
        (entry) => {
          changed =
            this.applyFacetEntryToParams(
              params,
              entry
            ) || changed;
        }
      );

      if (changed) {
        params.delete("page");
        params.delete("section_id");
      }

      return {
        params,
        entry: entries[0] || null,
        entries,
        allEntries: queryContext.entries,
        analysis: queryContext.analysis,
        changed
      };
    }

    paramsContainFacetEntry(
      paramsInput,
      entry
    ) {
      const params =
        paramsInput instanceof URLSearchParams
          ? paramsInput
          : new URLSearchParams(
              String(paramsInput || "").replace(/^\?/, "")
            );

      const paramName = String(
        entry?.filter_param || ""
      ).trim();

      const filterValue =
        this.normalizeTerm(
          entry?.filter_value
        );

      if (!paramName || !filterValue) {
        return false;
      }

      return params
        .getAll(paramName)
        .some(
          (value) =>
            this.normalizeTerm(value) ===
            filterValue
        );
    }

    removeFacetEntryFromParams(
      paramsInput,
      entry
    ) {
      const params =
        paramsInput instanceof URLSearchParams
          ? new URLSearchParams(paramsInput)
          : new URLSearchParams(
              String(paramsInput || "").replace(/^\?/, "")
            );

      const paramName = String(
        entry?.filter_param || ""
      ).trim();

      const filterValue =
        this.normalizeTerm(
          entry?.filter_value
        );

      if (!paramName || !filterValue) {
        return params;
      }

      const remainingValues = params
        .getAll(paramName)
        .filter(
          (value) =>
            this.normalizeTerm(value) !==
            filterValue
        );

      params.delete(paramName);

      remainingValues.forEach((value) => {
        params.append(paramName, value);
      });

      return params;
    }

    removeFacetEntriesFromParams(paramsInput, entries) {
      let params =
        paramsInput instanceof URLSearchParams
          ? new URLSearchParams(paramsInput)
          : new URLSearchParams(
              String(paramsInput || "").replace(/^\?/, "")
            );

      const before = params.toString();

      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        params = this.removeFacetEntryFromParams(params, entry);
      });

      return {
        params,
        changed: before !== params.toString()
      };
    }

    getUniqueMappedEntries(matches) {
      const seenUrls = new Set();

      return matches.filter((entry) => {
        const key = this.normalizeComparableUrl(
          entry?.url
        );

        if (!key || seenUrls.has(key)) {
          return false;
        }

        seenUrls.add(key);
        return true;
      });
    }

    normalizeComparableUrl(value) {
      if (!value) {
        return "";
      }

      try {
        const url = new URL(
          value,
          window.location.origin
        );

        /*
         * Shopifys søgeresultater kan føje sporingsparametre til
         * ellers identiske side- og artikel-URL'er, eksempelvis:
         * _pos, _sid og _ss.
         *
         * De må ikke gøre et indholdselement unikt i dubletkontrollen.
         * Bevar derimod funktionelle parametre som filter.*, sort_by
         * og øvrige værdier, der faktisk ændrer sidens indhold.
         */
        const ignoredTrackingParameters =
          new Set([
            "_pos",
            "_sid",
            "_ss",
            "fbclid",
            "gclid",
            "gad_source"
          ]);

        const parameters = [
          ...url.searchParams.entries()
        ]
          .filter(([key]) => {
            const normalizedKey =
              String(key)
                .toLocaleLowerCase("en-US");

            return (
              !ignoredTrackingParameters.has(
                normalizedKey
              ) &&
              !normalizedKey.startsWith("utm_")
            );
          })
          .sort(
            ([keyA, valueA], [keyB, valueB]) => {
              const keyResult =
                keyA.localeCompare(keyB);

              return (
                keyResult ||
                valueA.localeCompare(valueB)
              );
            }
          );

        url.search = "";
        url.hash = "";

        parameters.forEach(
          ([key, parameterValue]) => {
            url.searchParams.append(
              key,
              parameterValue
            );
          }
        );

        return `${
          url.pathname.replace(/\/$/, "")
        }${url.search}`;
      } catch (error) {
        return String(value || "");
      }
    }

    async render() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      const { signal } = this.abortController;

      this.setLoading(true);

      try {
        const queryContext =
          this.getQueryFacetContext(
            this.query
          );

        const { analysis } = queryContext;

        const mapMatches =
          this.getSearchMapMatches(
            this.query,
            {
              allowPrefix: true
            }
          );

        const combinedMatches =
          this.getUniqueEntries([
            ...analysis.facetEntries,
            ...mapMatches
          ]);

        const isNewProductsSearch =
          this.isNewProductsQuery(
            this.query
          );

        let sourceDescriptors = [];

        if (isNewProductsSearch) {
          /*
           * "Nye produkter" er en katalogvisning forklædt som søgning:
           * hele kataloget indlæses nedenfor, så der er ingen grund til at
           * hente almindelige titel-/map-kilder for selve søgestrengen.
           */
          sourceDescriptors = [];
        } else if (analysis.isIntentOnly) {
          const hasOrganicIntent =
            queryContext.entries.some(
              (entry) =>
                entry?.kind === "organic"
            );

          const isPureTypeIntent =
            queryContext.entries.length === 1 &&
            queryContext.entries[0]?.kind === "type";

          sourceDescriptors = [
            {
              url: this.buildFacetCollectionUrl(
                queryContext.entries,
                {
                  sectionId: this.sectionId,
                  sortBy: hasOrganicIntent
                    ? "title-ascending"
                    : isPureTypeIntent
                      ? "best-selling"
                      : null
                }
              ),
              searchTier: "facet"
            }
          ];
        } else {
          sourceDescriptors = [
            ...this.getProductSearchSources(
              analysis
            )
          ];

          const mappedEntries =
            this.getUniqueMappedEntries(
              analysis.facetEntries.length
                ? []
                : combinedMatches
            );

          sourceDescriptors.push(
            ...mappedEntries
              .map((entry) => ({
                url: this.buildMappedProductsUrl(
                  entry
                ),
                entry,
                facetEntries: entry ? [entry] : [],
                searchTier: "mapped",
                productRank: 40,
                sortAlphabetically: true
              }))
              .filter((source) => Boolean(source.url))
          );
        }

        const seenSourceUrls = new Set();

        sourceDescriptors = sourceDescriptors
          .filter((source) => Boolean(source?.url))
          .filter((source) => {
            const comparableUrl =
              this.normalizeComparableUrl(
                source.url
              );

            if (
              !comparableUrl ||
              seenSourceUrls.has(
                comparableUrl
              )
            ) {
              return false;
            }

            seenSourceUrls.add(
              comparableUrl
            );
            return true;
          });

        const catalogPagesPromise =
          this.fetchAllSourcePages(
            this.buildCatalogProductsUrl(),
            signal
          ).catch((error) => {
            if (error.name === "AbortError") {
              throw error;
            }

            console.warn(
              "Det globale produktkatalog kunne ikke indlæses til facetnavigation:",
              error
            );
            return [];
          });

        let sourceResults =
          await Promise.all(
            sourceDescriptors.map(async (source) => ({
              ...source,
              pages: await this.fetchAllSourcePages(
                source.url,
                signal
              )
            }))
          );

        const catalogPages = await catalogPagesPromise;

        this.catalogProducts = catalogPages.flatMap((page) =>
          Array.isArray(page?.products) ? page.products : []
        );

        const directTitleProductCount =
          this.countProductsInSourceResults(
            sourceResults,
            ["title-exact", "title-prefix"]
          );

        /*
         * Shopifys søgeindeks læser ikke custom.search_synonyms. Hvis de
         * kontrollerede titelkilder ikke har fundet et direkte titelmatch,
         * indlæses derfor hele det lille produktkatalog og matches lokalt.
         * Det gør både Ho Wood og generelle produktsynonymer pålidelige.
         */
        if (
          !analysis.isIntentOnly &&
          analysis.searchTerm
        ) {
          sourceResults = [
            ...sourceResults,
            ...this.createCatalogSourceResults(
              catalogPages,
              analysis
            )
          ];
        }

        const selectedSourceResults =
          analysis.isIntentOnly
            ? sourceResults
            : this.selectBestProductSourceResults(
                sourceResults
              );

        const mergedProducts =
          this.mergeProducts(
            selectedSourceResults
          );

        if (isNewProductsSearch) {
          this.allProducts =
            this.catalogProducts.map(
              (
                product,
                relevanceIndex
              ) => ({
                ...product,
                relevanceIndex
              })
            );

          this.contentMatches = {
            pages: [],
            articles: []
          };
        } else {
          this.allProducts =
            this.filterProductsByIntentEntries(
              mergedProducts,
              analysis.productQualifierEntries
            ).map(
              (
                product,
                relevanceIndex
              ) => ({
                ...product,
                relevanceIndex
              })
            );

          const contentSearchTerm =
            analysis.searchTerm ||
            this.query;

          this.contentMatches =
            this.getContentMatches(
              contentSearchTerm
            );
        }

        this.dataReady = true;
        this.ensureOlfactiveFacetOptions();

        const nextParamsInput =
          this.pendingFacetSearchParams ||
          window.location.search.slice(1);

        this.pendingFacetSearchParams = null;

        const resolvedParams = new URLSearchParams(
          String(nextParamsInput || "").replace(/^\?/, "")
        );

        /*
         * En fri søgning er kontekst, ikke en facet. Fjern derfor alle
         * produktfilter-parametre fra /search. q og sort_by bevares, mens
         * sidebarens valg senere navigerer ud i kataloget.
         */
        Array.from(resolvedParams.keys()).forEach((key) => {
          if (key.startsWith("filter.")) {
            resolvedParams.delete(key);
          }
        });

        resolvedParams.delete("page");
        resolvedParams.delete("section_id");

        this.root.removeAttribute(
          "data-esensera-soft-intent-fallback"
        );

        this.applyFacetSearchParams(
          resolvedParams,
          {
            updateHistory: false,
            initialRender: true
          }
        );

        if (
          window.location.search.slice(1) !==
          this.currentSearchParams.toString()
        ) {
          this.replaceHistory(this.currentSearchParams);
        }
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        console.error(
          "ESENSERA full search fejlede:",
          error
        );

        this.renderError();
      } finally {
        this.root.removeAttribute(
          "data-esensera-custom-search-pending"
        );
        this.setLoading(false);
      }
    }

    buildProductSearchUrl(
      searchTerm,
      mode = "broad"
    ) {
      const normalizedTerm =
        this.normalizeTerm(searchTerm);

      if (!normalizedTerm) {
        return null;
      }

      const url = new URL(
        this.getSearchRoute(),
        window.location.origin
      );

      let query = normalizedTerm;
      let prefixOption = "last";

      if (mode === "title-exact") {
        query = this.buildTitleFieldQuery(
          normalizedTerm,
          { prefix: false }
        );
        prefixOption = "none";
      } else if (
        mode === "title-prefix"
      ) {
        query = this.buildTitleFieldQuery(
          normalizedTerm,
          { prefix: true }
        );
        prefixOption = "none";
      }

      if (!query) {
        return null;
      }

      url.searchParams.set("q", query);
      url.searchParams.set("type", "product");
      url.searchParams.set(
        "options[prefix]",
        prefixOption
      );
      url.searchParams.set(
        "options[unavailable_products]",
        "show"
      );
      url.searchParams.set(
        "options[fields]",
        "title,product_type,tag,variants.title,variants.sku"
      );
      url.searchParams.set(
        "section_id",
        this.sectionId
      );

      return url.toString();
    }

    getProductSearchSources(analysis) {
      const searchTerm = this.normalizeTerm(analysis?.searchTerm);

      if (!searchTerm) {
        return [];
      }

      const allowPrefixMatches =
        this.shouldAllowProductPrefixMatches(
          analysis?.originalTerm || searchTerm
        );

      const getLocalTitleRank = (product) => {
        return this.getCatalogTitleMatchRank(product?.title, searchTerm);
      };

      const candidates = [
        {
          url: this.buildProductSearchUrl(
            searchTerm,
            "title-exact"
          ),
          searchTier: "title-exact",
          productPredicate: (product) => {
            const rank = getLocalTitleRank(product);
            return rank !== null && rank <= 1;
          }
        },
        {
          url: this.buildProductSearchUrl(
            searchTerm,
            "title-prefix"
          ),
          searchTier: "title-prefix",
          productPredicate: (product) => {
            const rank = getLocalTitleRank(product);

            if (rank === null) {
              return false;
            }

            return rank <= (allowPrefixMatches ? 2 : 1);
          }
        }
      ].filter((source) => Boolean(source.url));

      const seen = new Set();

      return candidates.filter((source) => {
        const comparableUrl =
          this.normalizeComparableUrl(source.url);

        if (!comparableUrl || seen.has(comparableUrl)) {
          return false;
        }

        seen.add(comparableUrl);
        return true;
      });
    }

    buildCatalogProductsUrl() {
      const allProductsUrl =
        this.searchMap?.all_products_url ||
        `${window.Shopify?.routes?.root || "/"}collections/all`;

      const url = new URL(
        allProductsUrl,
        window.location.origin
      );

      url.searchParams.set("sort_by", "title-ascending");
      url.searchParams.set("section_id", this.sectionId);

      return url.toString();
    }

    getCatalogQueryVariants(analysis) {
      const sourceValues =
        typeof analysis === "string"
          ? [analysis]
          : [
              analysis?.searchTerm,
              ...(Array.isArray(analysis?.searchTermVariants)
                ? analysis.searchTermVariants
                : [])
            ];

      return sourceValues
        .map((value) => this.normalizeFuzzyTerm(value))
        .filter(Boolean)
        .filter((value, index, values) => values.indexOf(value) === index);
    }

    toSearchSynonymArray(value) {
      if (Array.isArray(value)) {
        return value.flatMap((item) => this.toSearchSynonymArray(item));
      }

      if (
        value === null ||
        value === undefined ||
        String(value).trim() === ""
      ) {
        return [];
      }

      if (typeof value === "object" && "value" in value) {
        return this.toSearchSynonymArray(value.value);
      }

      return String(value)
        .split(/\r?\n|;/u)
        .map((item) => item.trim())
        .filter(Boolean);
    }

    getCatalogTitleMatchRank(title, normalizedQuery) {
      const normalizedTitle = this.normalizeFuzzyTerm(title);
      const query = this.normalizeFuzzyTerm(normalizedQuery);

      if (!normalizedTitle || !query) {
        return null;
      }

      if (normalizedTitle === query) {
        return 0;
      }

      if (` ${normalizedTitle} `.includes(` ${query} `)) {
        return 1;
      }

      const titleTokens = normalizedTitle.split(" ").filter(Boolean);
      const queryTokens = query.split(" ").filter(Boolean);

      if (
        queryTokens.length &&
        queryTokens.every((queryToken) => {
          return titleTokens.some((titleToken) => {
            return (
              titleToken === queryToken ||
              titleToken.startsWith(queryToken)
            );
          });
        })
      ) {
        return 2;
      }

      return null;
    }

    findTokenSequence(sourceTokens, targetTokens) {
      if (!sourceTokens.length || !targetTokens.length) {
        return -1;
      }

      for (
        let startIndex = 0;
        startIndex <= sourceTokens.length - targetTokens.length;
        startIndex += 1
      ) {
        const matches = targetTokens.every((targetToken, offset) => {
          return sourceTokens[startIndex + offset] === targetToken;
        });

        if (matches) {
          return startIndex;
        }
      }

      return -1;
    }

    getCatalogSynonymMatchRank(
      product,
      normalizedQuery,
      { allowPrefix = true } = {}
    ) {
      const query = this.normalizeFuzzyTerm(normalizedQuery);

      if (!query) {
        return null;
      }

      const queryTokens = query.split(" ").filter(Boolean);
      let bestRank = null;

      this.toSearchSynonymArray(product?.searchSynonyms).forEach(
        (synonym) => {
          const normalizedSynonym = this.normalizeFuzzyTerm(synonym);

          if (!normalizedSynonym) {
            return;
          }

          if (normalizedSynonym === query) {
            bestRank = bestRank === null ? 0 : Math.min(bestRank, 0);
            return;
          }

          if (
            allowPrefix &&
            query.length >= this.minimumQueryLength &&
            normalizedSynonym.startsWith(query)
          ) {
            bestRank = bestRank === null ? 1 : Math.min(bestRank, 1);
            return;
          }

          const synonymTokens = normalizedSynonym
            .split(" ")
            .filter(Boolean);

          const sequenceIndex = this.findTokenSequence(
            queryTokens,
            synonymTokens
          );

          if (sequenceIndex < 0) {
            return;
          }

          const remainingTokens = [
            ...queryTokens.slice(0, sequenceIndex),
            ...queryTokens.slice(sequenceIndex + synonymTokens.length)
          ];

          if (
            remainingTokens.length &&
            this.getCatalogTitleMatchRank(
              product?.title,
              remainingTokens.join(" ")
            ) !== null
          ) {
            bestRank = bestRank === null ? 2 : Math.min(bestRank, 2);
          }
        }
      );

      return bestRank;
    }

    getComparableWordTokens(value) {
      return this.normalizeFuzzyTerm(value)
        .split(" ")
        .filter(Boolean);
    }

    hasExactWordSequence(value, query) {
      const sourceTokens = this.getComparableWordTokens(value);
      const queryTokens = this.getComparableWordTokens(query);

      if (!sourceTokens.length || !queryTokens.length) {
        return false;
      }

      return this.findTokenSequence(sourceTokens, queryTokens) >= 0;
    }

    getCatalogBodyMatchRank(product, normalizedQuery) {
      const query = this.normalizeFuzzyTerm(normalizedQuery);

      if (!query || !String(product?.searchBody || "").trim()) {
        return null;
      }

      /*
       * Product descriptions are secondary discovery. Match only whole
       * words/phrases so fragments never explode into unrelated results.
       */
      return this.hasExactWordSequence(product.searchBody, query) ? 0 : null;
    }

    getCatalogProductMatch(product, analysis) {
      const variants = this.getCatalogQueryVariants(analysis);
      const allowPrefixMatches =
        this.shouldAllowProductPrefixMatches(
          analysis?.originalTerm || analysis?.searchTerm || variants[0] || ""
        );

      let bestTitleRank = null;

      variants.forEach((variant) => {
        const rank = this.getCatalogTitleMatchRank(product?.title, variant);

        if (
          rank !== null &&
          (rank <= 1 || allowPrefixMatches)
        ) {
          bestTitleRank =
            bestTitleRank === null ? rank : Math.min(bestTitleRank, rank);
        }
      });

      if (bestTitleRank !== null) {
        return {
          kind: "title",
          rank: bestTitleRank
        };
      }

      let bestSynonymRank = null;

      variants.forEach((variant) => {
        const rank = this.getCatalogSynonymMatchRank(product, variant, {
          allowPrefix: allowPrefixMatches
        });

        if (rank !== null) {
          bestSynonymRank =
            bestSynonymRank === null
              ? rank
              : Math.min(bestSynonymRank, rank);
        }
      });

      if (bestSynonymRank !== null) {
        return {
          kind: "synonym",
          rank: bestSynonymRank
        };
      }

      let bestBodyRank = null;

      variants.forEach((variant) => {
        const rank = this.getCatalogBodyMatchRank(product, variant);

        if (rank !== null) {
          bestBodyRank =
            bestBodyRank === null ? rank : Math.min(bestBodyRank, rank);
        }
      });

      if (bestBodyRank !== null) {
        return {
          kind: "body",
          rank: bestBodyRank
        };
      }

      return null;
    }

    createCatalogSourceResults(pages, analysis) {
      const titleProducts = [];
      const synonymProducts = [];
      const bodyProducts = [];

      (Array.isArray(pages) ? pages : []).forEach((page) => {
        (Array.isArray(page?.products) ? page.products : []).forEach(
          (product) => {
            const match = this.getCatalogProductMatch(product, analysis);

            if (match?.kind === "title") {
              titleProducts.push(product);
            } else if (match?.kind === "synonym") {
              synonymProducts.push(product);
            } else if (match?.kind === "body") {
              bodyProducts.push(product);
            }
          }
        );
      });

      const sources = [];

      if (titleProducts.length) {
        sources.push({
          searchTier: "catalog-title",
          productRank: 15,
          sortAlphabetically: true,
          pages: [{ products: titleProducts }]
        });
      }

      if (synonymProducts.length) {
        sources.push({
          searchTier: "catalog-synonym",
          productRank: 30,
          sortAlphabetically: true,
          pages: [{ products: synonymProducts }]
        });
      }

      if (bodyProducts.length) {
        sources.push({
          searchTier: "catalog-body",
          productRank: 60,
          sortAlphabetically: true,
          pages: [{ products: bodyProducts }]
        });
      }

      return sources;
    }

    countProductsInSourceResults(sourceResults, tiers = null) {
      const allowedTiers = Array.isArray(tiers) ? new Set(tiers) : null;

      return (Array.isArray(sourceResults) ? sourceResults : []).reduce(
        (count, source) => {
          if (allowedTiers && !allowedTiers.has(source?.searchTier)) {
            return count;
          }

          const sourceFacetEntries = Array.isArray(source?.facetEntries)
            ? source.facetEntries
            : source?.entry
              ? [source.entry]
              : [];

          let products = (Array.isArray(source?.pages)
            ? source.pages
            : []
          ).flatMap((page) =>
            Array.isArray(page?.products) ? page.products : []
          );

          products = this.filterProductsByIntentEntries(
            products,
            sourceFacetEntries
          );

          if (typeof source?.productPredicate === "function") {
            products = products.filter((product) => {
              return source.productPredicate(product);
            });
          }

          return count + products.length;
        },
        0
      );
    }

    getProductSourceRank(source) {
      if (Number.isFinite(Number(source?.productRank))) {
        return Number(source.productRank);
      }

      const ranks = {
        "title-exact": 10,
        "catalog-title": 15,
        "title-prefix": 20,
        "catalog-synonym": 30,
        mapped: 40,
        "catalog-body": 60,
        broad: 70,
        facet: 80
      };

      return ranks[source?.searchTier] ?? 80;
    }

    buildTitleFieldQuery(
      searchTerm,
      { prefix = true } = {}
    ) {
      const words = this.normalizeTerm(
        searchTerm
      ).match(
        /[\p{L}\p{N}][\p{L}\p{N}'’\-]*/gu
      );

      if (!words?.length) {
        return "";
      }

      return words
        .map((word) => {
          const suffix = prefix
            ? "*"
            : "";

          return (
            `title:${this.escapeSearchToken(
              word
            )}${suffix}`
          );
        })
        .join(" ");
    }

    escapeSearchToken(value) {
      return String(value || "")
        .replace(/\\/g, "\\\\")
        .replace(/([:()])/g, "\\$1")
        .replace(/\*/g, "\\*");
    }

    buildMappedProductsUrl(entry) {
      if (!entry?.url) {
        return null;
      }

      try {
        const url = new URL(
          entry.url,
          window.location.origin
        );

        url.searchParams.set(
          "sort_by",
          entry.source_sort_by || "best-selling"
        );

        url.searchParams.set(
          "section_id",
          this.sectionId
        );

        return url.toString();
      } catch (error) {
        console.warn(
          "Ugyldig URL i ESENSERA search map:",
          entry.url
        );

        return null;
      }
    }

    selectBestProductSourceResults(sourceResults) {
      const results = Array.isArray(sourceResults) ? sourceResults : [];
      const selected = [];

      const nonEmptySourcesForTier = (tier) => {
        const tierSources = results.filter(
          (source) => source?.searchTier === tier
        );

        return this.countProductsInSourceResults(tierSources) > 0
          ? tierSources
          : [];
      };

      /*
       * Direkte titelmatch er første lag. Et katalogtitelmatch bruges kun,
       * når Shopifys titelsøgning ikke selv fandt produktet.
       */
      for (const tier of [
        "title-exact",
        "title-prefix",
        "catalog-title"
      ]) {
        const tierSources = nonEmptySourcesForTier(tier);

        if (tierSources.length) {
          selected.push(...tierSources);
          break;
        }
      }

      /*
       * Produktsynonymer kommer efter titler, mens map-baserede matches
       * som Olfactive character og Duftprofil kommer derefter. Dermed står
       * Rose-produkter før produkter, der blot har tagget Rose.
       */
      selected.push(...nonEmptySourcesForTier("catalog-synonym"));
      selected.push(...nonEmptySourcesForTier("mapped"));
      selected.push(...nonEmptySourcesForTier("catalog-body"));

      if (!selected.length) {
        return results;
      }

      return selected
        .map((source, sourceIndex) => ({
          ...source,
          sourceIndex
        }))
        .sort((sourceA, sourceB) => {
          return (
            this.getProductSourceRank(sourceA) -
              this.getProductSourceRank(sourceB) ||
            sourceA.sourceIndex - sourceB.sourceIndex
          );
        });
    }

    async fetchAllSourcePages(baseUrl, signal) {
      const firstPage =
        await this.fetchSourcePage(
          baseUrl,
          signal
        );

      const pages = [firstPage];

      const totalPages = Math.min(
        Number(firstPage.totalPages || 1),
        this.maximumSourcePages
      );

      if (totalPages <= 1) {
        return pages;
      }

      const additionalRequests = [];

      for (
        let page = 2;
        page <= totalPages;
        page += 1
      ) {
        const pageUrl = new URL(
          baseUrl,
          window.location.origin
        );

        pageUrl.searchParams.set(
          firstPage.pageParameter || "page",
          page
        );

        additionalRequests.push(
          this.fetchSourcePage(
            pageUrl,
            signal
          )
        );
      }

      pages.push(
        ...(await Promise.all(
          additionalRequests
        ))
      );

      return pages;
    }

    async fetchSourcePage(url, signal) {
      const response = await fetch(url, {
        signal,
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      if (!response.ok) {
        throw new Error(
          `Produktkilde: HTTP ${response.status}`
        );
      }

      const text = await response.text();

      const documentResult =
        new DOMParser().parseFromString(
          text,
          "text/html"
        );

      const sourceRoot =
        documentResult.querySelector(
          "[data-esensera-full-search-products]"
        );

      if (!sourceRoot) {
        throw new Error(
          "Produktsektionen manglede i svaret."
        );
      }

      const indexElement =
        sourceRoot.querySelector(
          "[data-esensera-product-index]"
        );

      const cardsContainer =
        sourceRoot.querySelector(
          "[data-esensera-product-cards]"
        );

      let index = [];

      try {
        index = JSON.parse(
          indexElement?.textContent?.trim() ||
            "[]"
        );
      } catch (error) {
        console.warn(
          "Produktindekset kunne ikke læses:",
          error
        );
      }

      const cards = cardsContainer
        ? Array.from(cardsContainer.children)
        : [];

      return {
        totalPages: Number(
          sourceRoot.dataset.totalPages || 1
        ),
        pageParameter:
          sourceRoot.dataset.pageParameter ||
          "page",
        products: index.map(
          (product, indexPosition) => ({
            ...product,
            card: cards[indexPosition]
              ? document.importNode(
                  cards[indexPosition],
                  true
                )
              : null
          })
        )
      };
    }

    mergeProducts(sourceResults) {
      const products = [];
      const seen = new Set();

      const orderedSources = (Array.isArray(sourceResults)
        ? sourceResults
        : []
      )
        .map((source, sourceIndex) => ({
          ...source,
          sourceIndex
        }))
        .sort((sourceA, sourceB) => {
          return (
            this.getProductSourceRank(sourceA) -
              this.getProductSourceRank(sourceB) ||
            sourceA.sourceIndex - sourceB.sourceIndex
          );
        });

      orderedSources.forEach((source) => {
        const sourceFacetEntries = Array.isArray(source?.facetEntries)
          ? source.facetEntries
          : source?.entry
            ? [source.entry]
            : [];

        let sourceProducts = (Array.isArray(source?.pages)
          ? source.pages
          : []
        ).flatMap((page) =>
          Array.isArray(page?.products) ? page.products : []
        );

        sourceProducts = this.filterProductsByIntentEntries(
          sourceProducts,
          sourceFacetEntries
        );

        if (typeof source?.productPredicate === "function") {
          sourceProducts = sourceProducts.filter((product) => {
            return source.productPredicate(product);
          });
        }

        const shouldSortAlphabetically =
          source?.sortAlphabetically === true ||
          [
            "title-exact",
            "title-prefix",
            "catalog-title",
            "catalog-synonym",
            "mapped"
          ].includes(source?.searchTier);

        if (shouldSortAlphabetically) {
          sourceProducts.sort((productA, productB) => {
            return String(productA?.title || "").localeCompare(
              String(productB?.title || ""),
              this.isDanish() ? "da" : "en",
              {
                sensitivity: "base",
                numeric: true
              }
            );
          });
        }

        sourceProducts.forEach((product) => {
          const key =
            product.id ||
            product.handle ||
            this.normalizeComparableUrl(product.url);

          if (!key || seen.has(String(key))) {
            return;
          }

          seen.add(String(key));

          products.push({
            ...product,
            sourceIndex: source.sourceIndex,
            sourceTier: source.searchTier || ""
          });
        });
      });

      return products;
    }

    readNativeContentMatches() {
      const pages = [];
      const articles = [];

      if (!this.productContainer) {
        return {
          pages,
          articles
        };
      }

      this.productContainer
        .querySelectorAll(
          '[data-search-result-group="pages"] [data-esensera-page-result]'
        )
        .forEach((card) => {
          const link = card.querySelector(
            ".esensera-search-page-title a"
          );

          const title = String(
            link?.textContent || ""
          ).trim();

          const url = String(
            link?.getAttribute("href") || ""
          ).trim();

          if (!title || !url) {
            return;
          }

          const contentTemplate =
            card.querySelector(
              "[data-esensera-page-content]"
            );

          const existingExcerpt =
            card.querySelector(
              "[data-esensera-page-excerpt]"
            );

          pages.push({
            type: "page",
            title,
            url,
            searchBody: String(
              contentTemplate?.content?.textContent ||
                contentTemplate?.textContent ||
                existingExcerpt?.textContent ||
                ""
            ).trim(),
            excerptHtml:
              contentTemplate?.innerHTML ||
              existingExcerpt?.innerHTML ||
              ""
          });
        });

      this.productContainer
        .querySelectorAll(
          '[data-search-result-group="articles"] .esensera-search-article-card'
        )
        .forEach((card) => {
          const link = card.querySelector("a");
          const heading = card.querySelector(
            ".esensera-search-article-title"
          );
          const image = card.querySelector("img");
          const contentTemplate = card.querySelector(
            "[data-esensera-article-content]"
          );

          const title = String(
            heading?.textContent || ""
          ).trim();

          const url = String(
            link?.getAttribute("href") || ""
          ).trim();

          if (!title || !url) {
            return;
          }

          articles.push({
            type: "article",
            title,
            url,
            searchBody: String(
              contentTemplate?.content?.textContent ||
                contentTemplate?.textContent ||
                ""
            ).trim(),
            imageUrl:
              image?.currentSrc ||
              image?.getAttribute("src") ||
              image?.getAttribute("data-src") ||
              ""
          });
        });

      return {
        pages,
        articles
      };
    }

    filterNativeContentMatches(searchTerm) {
      const query = this.normalizeFuzzyTerm(searchTerm);

      if (!query) {
        return { pages: [], articles: [] };
      }

      const isExcludedPage = (item) => {
        try {
          const url = new URL(item?.url || "", window.location.origin);
          const pathname = url.pathname
            .replace(/\/+$/, "")
            .toLocaleLowerCase("en-US");

          return new Set([
            "/pages/about",
            "/pages/contact",
            "/pages/scent-profiles"
          ]).has(pathname);
        } catch (error) {
          return false;
        }
      };

      const keepExactContentMatch = (item) => {
        return (
          !isExcludedPage(item) &&
          this.hasExactWordSequence(
            `${item?.title || ""} ${item?.searchBody || ""}`,
            query
          )
        );
      };

      return {
        pages: (Array.isArray(this.nativeContentMatches?.pages)
          ? this.nativeContentMatches.pages
          : []
        ).filter(keepExactContentMatch),
        articles: (Array.isArray(this.nativeContentMatches?.articles)
          ? this.nativeContentMatches.articles
          : []
        ).filter(keepExactContentMatch)
      };
    }

    mergeContentItems(
      primaryItems,
      secondaryItems
    ) {
      const mergedItems = [];
      const seen = new Set();

      [
        ...(Array.isArray(primaryItems)
          ? primaryItems
          : []),
        ...(Array.isArray(secondaryItems)
          ? secondaryItems
          : [])
      ].forEach((item) => {
        const key =
          this.normalizeComparableUrl(
            item?.url
          ) ||
          `${this.normalizeTerm(
            item?.type
          )}:${this.normalizeTerm(
            item?.title
          )}`;

        if (!key || seen.has(key)) {
          return;
        }

        seen.add(key);
        mergedItems.push(item);
      });

      return mergedItems;
    }

    mergeContentMatches(
      primaryMatches,
      secondaryMatches
    ) {
      return {
        pages: this.mergeContentItems(
          primaryMatches?.pages,
          secondaryMatches?.pages
        ),
        articles: this.mergeContentItems(
          primaryMatches?.articles,
          secondaryMatches?.articles
        )
      };
    }

    getExploreProductsPage() {
      const shopRoot =
        window.Shopify?.routes?.root ||
        "/";

      const normalizedRoot =
        shopRoot.endsWith("/")
          ? shopRoot
          : `${shopRoot}/`;

      return {
        type: "page",
        isExploreProducts: true,
        title: this.isDanish()
          ? "Udforsk vores produkter"
          : "Explore our products",
        url:
          `${normalizedRoot}collections/all`,
        excerptHtml: this.isDanish()
          ? "<p>Gå på opdagelse i Nordeuropas største og mest sofistikerede udvalg af naturlige aromatiske råmaterialer til professionelle.</p>"
          : "<p>Explore Northern Europe’s largest and most sophisticated selection of natural aromatic raw materials for professionals.</p>"
      };
    }

    getContentMatches(searchTerm) {
      if (
        !Array.isArray(
          this.contentIndex?.items
        )
      ) {
        return {
          pages: [],
          articles: []
        };
      }

      const normalizedSearchTerm =
        this.normalizeTerm(searchTerm);

      if (
        normalizedSearchTerm.length <
        this.minimumQueryLength
      ) {
        return {
          pages: [],
          articles: []
        };
      }

      const matchedItems =
        this.contentIndex.items
          .map((item) => {
            const matchScore =
              this.getContentMatchScore(
                item,
                normalizedSearchTerm
              );

            if (matchScore <= 0) {
              return null;
            }

            return {
              item,
              matchScore,
              priority: Number(
                item.priority || 0
              )
            };
          })
          .filter(Boolean)
          .sort((matchA, matchB) => {
            const scoreDifference =
              matchB.matchScore -
              matchA.matchScore;

            if (scoreDifference !== 0) {
              return scoreDifference;
            }

            const priorityDifference =
              matchB.priority -
              matchA.priority;

            if (
              priorityDifference !== 0
            ) {
              return priorityDifference;
            }

            return String(
              matchA.item.title || ""
            ).localeCompare(
              String(
                matchB.item.title || ""
              ),
              this.isDanish()
                ? "da"
                : "en"
            );
          });

      return {
        pages: matchedItems
          .filter(
            ({ item }) =>
              item.type === "page"
          )
          .map(({ item }) => item),

        articles: matchedItems
          .filter(
            ({ item }) =>
              item.type === "article"
          )
          .map(({ item }) => item)
      };
    }

    getContentMatchScore(
      item,
      normalizedSearchTerm
    ) {
      const normalizedTitle =
        this.normalizeTerm(item?.title);

      const normalizedTerms =
        Array.isArray(item?.terms)
          ? item.terms
              .map((term) =>
                this.normalizeTerm(term)
              )
              .filter(Boolean)
          : [];

      if (
        normalizedTitle ===
        normalizedSearchTerm
      ) {
        return 100;
      }

      if (
        normalizedTerms.some(
          (term) =>
            term === normalizedSearchTerm
        )
      ) {
        return 95;
      }

      if (
        normalizedTitle.startsWith(
          normalizedSearchTerm
        )
      ) {
        return 90;
      }

      if (
        this.hasWordPrefix(
          normalizedTitle,
          normalizedSearchTerm
        )
      ) {
        return 85;
      }

      if (
        normalizedTerms.some((term) =>
          term.startsWith(
            normalizedSearchTerm
          )
        )
      ) {
        return 80;
      }

      if (
        normalizedTitle.includes(
          normalizedSearchTerm
        )
      ) {
        return 70;
      }

      if (
        normalizedTerms.some((term) =>
          term.includes(
            normalizedSearchTerm
          )
        )
      ) {
        return 60;
      }

      return 0;
    }

    hasWordPrefix(value, searchTerm) {
      return String(value || "")
        .split(/[^\p{L}\p{N}]+/u)
        .filter(Boolean)
        .some((word) =>
          word.startsWith(searchTerm)
        );
    }

    applyFacetSearchParams(
      searchParamsInput,
      {
        updateHistory = true,
        initialRender = false
      } = {}
    ) {
      if (!this.canHandleFacets()) {
        return false;
      }

      const normalizedParams =
        this.normalizeFacetSearchParams(
          searchParamsInput
        );

      if (!this.dataReady) {
        this.pendingFacetSearchParams =
          normalizedParams.toString();

        return true;
      }

      this.setLoading(!initialRender);

      this.currentSearchParams =
        normalizedParams;

      this.ensureOlfactiveFacetOptions();
      this.syncFacetControls(
        normalizedParams
      );

      const filteredProducts =
        this.filterProducts(
          this.allProducts,
          normalizedParams
        );

      const requestedSort =
        normalizedParams.get(
          "sort_by"
        );

      const shouldUseNewProductsOrder =
        this.isNewProductsQuery(
          this.query
        ) &&
        (!requestedSort ||
          requestedSort ===
            "relevance");

      const sortedProducts =
        shouldUseNewProductsOrder
          ? this.sortNewProductsFirst(
              filteredProducts
            )
          : this.sortProducts(
              filteredProducts,
              requestedSort
            );

      const hasProductSearchMatches =
        this.allProducts.length > 0;

      this.renderResults({
        products: sortedProducts,
        pages: this.contentMatches.pages,
        articles:
          this.contentMatches.articles,
        hasProductSearchMatches
      });

      /*
       * Facetterne er katalog-navigation og skal derfor være brugbare også
       * ved en fri søgning med 0 produktmatches. Tilgængelighed/counts
       * beregnes mod hele kataloget i updateFacetCounts().
       */
      this.updateFacetInterface(
        normalizedParams
      );

      if (hasProductSearchMatches) {
        void this.updateCollectionContext(
          normalizedParams
        );
      } else {
        this.clearCollectionContext();
      }

      this.scheduleActiveFacetRefresh(
        normalizedParams
      );

      if (updateHistory) {
        this.updateHistory(
          normalizedParams
        );
      }

      this.root.setAttribute(
        "data-esensera-custom-facets-ready",
        "true"
      );

      this.setLoading(false);

      if (
        typeof initLazyloadItem ===
        "function"
      ) {
        initLazyloadItem();
      }

      if (
        typeof initializeScrollAnimationTrigger ===
        "function"
      ) {
        initializeScrollAnimationTrigger();
      }

      return true;
    }

    normalizeFacetSearchParams(
      searchParamsInput
    ) {
      const params =
        searchParamsInput instanceof
        URLSearchParams
          ? new URLSearchParams(
              searchParamsInput
            )
          : new URLSearchParams(
              String(
                searchParamsInput || ""
              ).replace(/^\?/, "")
            );

      params.delete("section_id");
      params.delete("page");

      if (!params.has("q")) {
        params.set("q", this.query);
      }

      const typeValues = params.getAll(
        FILTER_PARAM_NAMES.type
      );

      params.delete(
        FILTER_PARAM_NAMES.type
      );

      if (typeValues.length) {
        const activeTypeHandle =
          this.resolveTypeHandle(
            typeValues[
              typeValues.length - 1
            ]
          );

        if (
          activeTypeHandle &&
          activeTypeHandle !== "all"
        ) {
          params.set(
            FILTER_PARAM_NAMES.type,
            TYPE_LABELS[
              activeTypeHandle
            ] ||
              typeValues[
                typeValues.length - 1
              ]
          );
        }
      }

      const scentValues = params.getAll(
        FILTER_PARAM_NAMES.scentProfile
      );

      if (scentValues.length > 1) {
        params.delete(
          FILTER_PARAM_NAMES.scentProfile
        );

        params.set(
          FILTER_PARAM_NAMES.scentProfile,
          scentValues[
            scentValues.length - 1
          ]
        );
      }

      const aromatherapyValues =
        params.getAll(
          FILTER_PARAM_NAMES.aromatherapy
        );

      const organicValues = params.getAll(
        FILTER_PARAM_NAMES.organic
      );

      if (aromatherapyValues.length > 1) {
        params.delete(
          FILTER_PARAM_NAMES.aromatherapy
        );

        params.set(
          FILTER_PARAM_NAMES.aromatherapy,
          aromatherapyValues[
            aromatherapyValues.length - 1
          ]
        );
      }

      if (organicValues.length > 1) {
        params.delete(
          FILTER_PARAM_NAMES.organic
        );

        params.set(
          FILTER_PARAM_NAMES.organic,
          organicValues[
            organicValues.length - 1
          ]
        );
      }

      const activeTypeHandle =
        this.getActiveTypeHandle(params);

      if (
        activeTypeHandle ===
          "perfumery-bases" ||
        activeTypeHandle === "blends"
      ) {
        params.delete(
          FILTER_PARAM_NAMES.scentProfile
        );
      }

      if (
        activeTypeHandle === "blends"
      ) {
        params.delete(
          FILTER_PARAM_NAMES.note
        );
      }

      if (
        activeTypeHandle ===
        "perfumery-bases"
      ) {
        params.delete(
          FILTER_PARAM_NAMES.aromatherapy
        );
      }

      return params;
    }

    updateHistory(params) {
      const searchParams =
        params.toString();

      const nextUrl = searchParams
        ? `${window.location.pathname}?${searchParams}`
        : window.location.pathname;

      window.history.pushState(
        {
          searchParams
        },
        "",
        nextUrl
      );
    }

    replaceHistory(params) {
      const searchParams =
        params.toString();

      const nextUrl = searchParams
        ? `${window.location.pathname}?${searchParams}`
        : window.location.pathname;

      window.history.replaceState(
        {
          searchParams
        },
        "",
        nextUrl
      );
    }

    resolveTypeHandle(value) {
      const normalizedValue =
        this.normalizeTerm(value);

      if (
        !normalizedValue ||
        normalizedValue === "all" ||
        normalizedValue ===
          this.normalizeTerm(
            "Alle produkter"
          ) ||
        normalizedValue ===
          this.normalizeTerm(
            "All products"
          )
      ) {
        return "all";
      }

      const directHandle =
        Object.keys(TYPE_LABELS).find(
          (handle) =>
            this.normalizeTerm(handle) ===
            normalizedValue
        );

      if (directHandle) {
        return directHandle;
      }

      return (
        Object.keys(TYPE_ALIASES).find(
          (handle) =>
            TYPE_ALIASES[handle].some(
              (alias) =>
                this.normalizeTerm(
                  alias
                ) === normalizedValue
            )
        ) || null
      );
    }

    getActiveTypeHandle(params) {
      const value = params.get(
        FILTER_PARAM_NAMES.type
      );

      return (
        this.resolveTypeHandle(value) ||
        "all"
      );
    }

    productMatchesIntentEntry(product, entry) {
      const paramName = String(
        entry?.filter_param || ""
      ).trim();
      const filterValue = String(
        entry?.filter_value || ""
      ).trim();

      if (!paramName || !filterValue) {
        return true;
      }

      if (paramName === FILTER_PARAM_NAMES.type) {
        const typeHandle =
          this.resolveTypeHandle(entry?.handle) ||
          this.resolveTypeHandle(filterValue);

        return typeHandle
          ? this.productMatchesType(product, typeHandle)
          : false;
      }

      const productFieldByParameter = {
        [FILTER_PARAM_NAMES.scentProfile]: "scentProfile",
        [FILTER_PARAM_NAMES.note]: "perfumeryNote",
        [FILTER_PARAM_NAMES.aromatherapy]: "aromatherapy",
        [FILTER_PARAM_NAMES.organic]: "customProductBadge",
        [FILTER_PARAM_NAMES.olfactive]: "tags"
      };

      const productField =
        productFieldByParameter[paramName];

      if (!productField) {
        return true;
      }

      return this.productMatchesValues(
        product?.[productField],
        [filterValue]
      );
    }

    filterProductsByIntentEntries(products, entries) {
      const intentEntries = Array.isArray(entries) ? entries : [];

      if (!intentEntries.length) {
        return products;
      }

      return products.filter((product) =>
        intentEntries.every((entry) =>
          this.productMatchesIntentEntry(product, entry)
        )
      );
    }

    filterProducts(
      products,
      params,
      excludedRole = null
    ) {
      const activeTypeHandle =
        this.getActiveTypeHandle(params);

      const scentProfiles =
        params.getAll(
          FILTER_PARAM_NAMES.scentProfile
        );

      const notes = params.getAll(
        FILTER_PARAM_NAMES.note
      );

      const aromatherapyValues =
        params.getAll(
          FILTER_PARAM_NAMES.aromatherapy
        );

      const organicValues = params.getAll(
        FILTER_PARAM_NAMES.organic
      );

      const olfactiveValues =
        params.getAll(
          FILTER_PARAM_NAMES.olfactive
        );

      const minimumPrice =
        this.parsePriceFilter(
          params.get(
            FILTER_PARAM_NAMES.priceMinimum
          )
        );

      const maximumPrice =
        this.parsePriceFilter(
          params.get(
            FILTER_PARAM_NAMES.priceMaximum
          )
        );

      return products.filter((product) => {
        if (
          excludedRole !== "type" &&
          activeTypeHandle !== "all" &&
          !this.productMatchesType(
            product,
            activeTypeHandle
          )
        ) {
          return false;
        }

        if (
          excludedRole !==
            "scent-profile" &&
          scentProfiles.length &&
          !this.productMatchesValues(
            product.scentProfile,
            scentProfiles
          )
        ) {
          return false;
        }

        if (
          excludedRole !== "note" &&
          notes.length &&
          !this.productMatchesValues(
            product.perfumeryNote,
            notes
          )
        ) {
          return false;
        }

        if (
          excludedRole !==
            "aromatherapy" &&
          aromatherapyValues.length &&
          !this.productMatchesValues(
            product.aromatherapy,
            aromatherapyValues
          )
        ) {
          return false;
        }

        if (
          excludedRole !== "organic" &&
          organicValues.length &&
          !this.productMatchesValues(
            product.customProductBadge,
            organicValues
          )
        ) {
          return false;
        }

        if (
          excludedRole !==
            "olfactive-character" &&
          olfactiveValues.length &&
          !this.productMatchesValues(
            product.tags,
            olfactiveValues,
            "all"
          )
        ) {
          return false;
        }

        const productPrice = Number(
          product.price || 0
        );

        if (
          excludedRole !== "price" &&
          minimumPrice !== null &&
          productPrice < minimumPrice
        ) {
          return false;
        }

        if (
          excludedRole !== "price" &&
          maximumPrice !== null &&
          productPrice > maximumPrice
        ) {
          return false;
        }

        return true;
      });
    }

    productMatchesType(
      product,
      typeHandle
    ) {
      const collectionHandles =
        this.toValueArray(
          product.collectionHandles
        ).map((value) =>
          this.normalizeTerm(value)
        );

      if (
        collectionHandles.includes(
          this.normalizeTerm(typeHandle)
        )
      ) {
        return true;
      }

      const productType =
        this.normalizeTerm(
          product.productType
        );

      return (
        TYPE_ALIASES[typeHandle] || []
      ).some(
        (alias) =>
          this.normalizeTerm(alias) ===
          productType
      );
    }

    productMatchesValues(
      productValue,
      selectedValues,
      matchMode = "any"
    ) {
      const productValues =
        this.toValueArray(productValue)
          .map((value) =>
            this.normalizeTerm(value)
          )
          .filter(Boolean);

      const normalizedSelectedValues =
        this.toValueArray(
          selectedValues
        )
          .map((value) =>
            this.normalizeTerm(value)
          )
          .filter(Boolean);

      if (!normalizedSelectedValues.length) {
        return true;
      }

      const matcher =
        matchMode === "all"
          ? "every"
          : "some";

      return normalizedSelectedValues[matcher](
        (selectedValue) =>
          productValues.includes(
            selectedValue
          )
      );
    }

    toValueArray(value) {
      if (Array.isArray(value)) {
        return value.flatMap((item) =>
          this.toValueArray(item)
        );
      }

      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return [];
      }

      if (
        typeof value === "object" &&
        "value" in value
      ) {
        return this.toValueArray(
          value.value
        );
      }

      const stringValue = String(value);

      if (stringValue.includes(",")) {
        return stringValue
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return [stringValue];
    }

    parsePriceFilter(value) {
      if (
        value === null ||
        value === undefined ||
        String(value).trim() === ""
      ) {
        return null;
      }

      const numberValue = Number(
        String(value)
          .replace(/\s/g, "")
          .replace(",", ".")
      );

      if (!Number.isFinite(numberValue)) {
        return null;
      }

      return Math.round(
        numberValue * 100
      );
    }

    sortNewProductsFirst(products) {
      const productsCopy = [
        ...products
      ];

      const now = Date.now();
      const cutoff =
        now - 30 * 24 * 60 * 60 * 1000;

      const byTitle = (
        productA,
        productB
      ) =>
        String(
          productA.title || ""
        ).localeCompare(
          String(
            productB.title || ""
          ),
          this.isDanish() ? "da" : "en",
          {
            sensitivity: "base",
            numeric: true
          }
        );

      const recentProducts = [];
      const establishedProducts = [];

      productsCopy.forEach((product) => {
        const publishedTimestamp =
          this.getTimestamp(
            product.publishedAt
          );

        if (
          publishedTimestamp >= cutoff &&
          publishedTimestamp <= now
        ) {
          recentProducts.push(product);
          return;
        }

        establishedProducts.push(product);
      });

      recentProducts.sort(
        (productA, productB) =>
          this.getTimestamp(
            productB.publishedAt
          ) -
            this.getTimestamp(
              productA.publishedAt
            ) ||
          byTitle(
            productA,
            productB
          )
      );

      establishedProducts.sort(byTitle);

      return [
        ...recentProducts,
        ...establishedProducts
      ];
    }

    sortProducts(products, sortBy) {
      const sortedProducts = [
        ...products
      ];

      const normalizedSort =
        String(sortBy || "relevance");

      const byRelevance = (
        productA,
        productB
      ) =>
        Number(
          productA.relevanceIndex || 0
        ) -
        Number(
          productB.relevanceIndex || 0
        );

      const byTitle = (
        productA,
        productB
      ) =>
        String(
          productA.title || ""
        ).localeCompare(
          String(
            productB.title || ""
          ),
          this.isDanish() ? "da" : "en",
          {
            sensitivity: "base"
          }
        );

      const byPrice = (
        productA,
        productB
      ) =>
        Number(productA.price || 0) -
        Number(productB.price || 0);

      const byDate = (
        productA,
        productB
      ) =>
        this.getTimestamp(
          productA.publishedAt
        ) -
        this.getTimestamp(
          productB.publishedAt
        );

      switch (normalizedSort) {
        case "price-ascending":
          sortedProducts.sort(
            (productA, productB) =>
              byPrice(
                productA,
                productB
              ) ||
              byTitle(
                productA,
                productB
              )
          );
          break;

        case "price-descending":
          sortedProducts.sort(
            (productA, productB) =>
              byPrice(
                productB,
                productA
              ) ||
              byTitle(
                productA,
                productB
              )
          );
          break;

        case "title-ascending":
          sortedProducts.sort(byTitle);
          break;

        case "title-descending":
          sortedProducts.sort(
            (productA, productB) =>
              byTitle(
                productB,
                productA
              )
          );
          break;

        case "created-ascending":
        case "date-ascending":
          sortedProducts.sort(
            (productA, productB) =>
              byDate(
                productA,
                productB
              ) ||
              byTitle(
                productA,
                productB
              )
          );
          break;

        case "created-descending":
        case "date-descending":
          sortedProducts.sort(
            (productA, productB) =>
              byDate(
                productB,
                productA
              ) ||
              byTitle(
                productA,
                productB
              )
          );
          break;

        default:
          sortedProducts.sort(
            byRelevance
          );
      }

      return sortedProducts;
    }

    getTimestamp(value) {
      const timestamp = Date.parse(
        String(value || "")
      );

      return Number.isFinite(timestamp)
        ? timestamp
        : 0;
    }

    ensureOlfactiveFacetOptions() {
      const definitions =
        this.getOlfactiveDefinitions();

      if (!definitions.length) {
        return;
      }

      document
        .querySelectorAll(
          "#FacetsWrapperDesktop, #FacetsWrapperMobile"
        )
        .forEach((wrapper) => {
          const filterElement =
            Array.from(
              wrapper.querySelectorAll(
                "action-filter.js-filter"
              )
            ).find((filter) => {
              return (
                filter.querySelector(
                  `input[name="${FILTER_PARAM_NAMES.olfactive}"]`
                ) ||
                String(
                  filter.dataset.index || ""
                ).includes(
                  "olfactive"
                )
              );
            });

          if (!filterElement) {
            return;
          }

          const list =
            filterElement.querySelector(
              "fieldset ul"
            );

          if (!list) {
            return;
          }

          const isMobile =
            wrapper.id ===
            "FacetsWrapperMobile";

          const prototypeInput =
            wrapper.querySelector(
              'label.facet-checkbox input[type="checkbox"]'
            );

          const visualNodes =
            this.getCheckboxVisualNodes(
              prototypeInput
            );

          list.replaceChildren();

          definitions.forEach(
            (definition, index) => {
              list.appendChild(
                this.createFacetOption({
                  definition,
                  index,
                  isMobile,
                  visualNodes
                })
              );
            }
          );

          filterElement.dataset
            .esenseraFacetRole =
            "olfactive-character";
        });
    }

    getOlfactiveDefinitions() {
      if (
        !Array.isArray(
          this.searchMap?.entries
        )
      ) {
        return [];
      }

      const definitions = [];
      const seen = new Set();

      this.searchMap.entries
        .filter(
          (entry) =>
            entry.kind ===
            "olfactive_character"
        )
        .forEach((entry) => {
          if (!entry.url) {
            return;
          }

          try {
            const url = new URL(
              entry.url,
              window.location.origin
            );

            const values =
              url.searchParams.getAll(
                FILTER_PARAM_NAMES.olfactive
              );

            values.forEach((value) => {
              const key =
                this.normalizeTerm(value);

              if (!key || seen.has(key)) {
                return;
              }

              seen.add(key);

              definitions.push({
                value,
                label:
                  entry.label || value
              });
            });
          } catch (error) {
            console.warn(
              "Olfactive character kunne ikke læses:",
              entry.url
            );
          }
        });

      return definitions;
    }

    getCheckboxVisualNodes(
      prototypeInput
    ) {
      if (!prototypeInput) {
        return [];
      }

      const visualNodes = [];
      let element =
        prototypeInput.nextElementSibling;

      while (
        element &&
        visualNodes.length < 2
      ) {
        visualNodes.push(
          element.cloneNode(true)
        );

        element =
          element.nextElementSibling;
      }

      return visualNodes;
    }

    createFacetOption({
      definition,
      index,
      isMobile,
      visualNodes
    }) {
      const listItem =
        document.createElement("li");

      listItem.className =
        "list-menu__item facets__item";

      const label =
        document.createElement("label");

      label.className =
        "facet-checkbox d-flex align-center break-word";

      const input =
        document.createElement("input");

      const id = `${
        isMobile
          ? "Facet-Mobile"
          : "Filter"
      }-Esensera-Olfactive-${index + 1}`;

      input.type = "checkbox";
      input.name =
        FILTER_PARAM_NAMES.olfactive;
      input.value = definition.value;
      input.id = id;

      label.htmlFor = id;
      label.appendChild(input);

      if (visualNodes.length) {
        visualNodes.forEach((node) => {
          label.appendChild(
            node.cloneNode(true)
          );
        });
      } else {
        const checkbox =
          document.createElement("span");

        checkbox.className = "checkbox";
        label.appendChild(checkbox);
      }

      const content =
        document.createElement("span");

      content.className =
        "d-inline-flex flex-1 gap-10 align-center space-between fs-small-1";

      const text =
        document.createElement("span");

      text.textContent =
        definition.label;

      const count =
        document.createElement("span");

      count.className = "count nowrap";
      count.textContent = "0";

      content.appendChild(text);
      content.appendChild(count);
      label.appendChild(content);

      const accessible =
        document.createElement("span");

      accessible.className =
        "visually-hidden";

      accessible.textContent =
        `${definition.label} (0 produkter)`;

      label.appendChild(accessible);
      listItem.appendChild(label);

      return listItem;
    }

    syncFacetControls(params) {
      const activeTypeHandle =
        this.getActiveTypeHandle(params);

      document
        .querySelectorAll(
          ".js-type-option[data-type-handle]"
        )
        .forEach((option) => {
          const checkbox =
            option.querySelector(
              ".js-type-collection-checkbox"
            );

          if (!checkbox) {
            return;
          }

          const isActive =
            option.dataset.typeHandle ===
            activeTypeHandle;

          checkbox.checked = isActive;
          option.dataset.typeActive =
            isActive
              ? "true"
              : "false";

          if (isActive) {
            option.setAttribute(
              "aria-current",
              "page"
            );
          } else {
            option.removeAttribute(
              "aria-current"
            );
          }
        });

      [
        FILTER_PARAM_NAMES.scentProfile,
        FILTER_PARAM_NAMES.note,
        FILTER_PARAM_NAMES.aromatherapy,
        FILTER_PARAM_NAMES.organic,
        FILTER_PARAM_NAMES.olfactive
      ].forEach((paramName) => {
        const activeValues = params
          .getAll(paramName)
          .map((value) =>
            this.normalizeTerm(value)
          );

        document
          .querySelectorAll(
            `input[name="${paramName}"]`
          )
          .forEach((input) => {
            input.checked =
              activeValues.includes(
                this.normalizeTerm(
                  input.value
                )
              );
          });
      });

      const sortBy =
        params.get("sort_by") ||
        "relevance";

      document
        .querySelectorAll(
          'select[name="sort_by"]'
        )
        .forEach((select) => {
          if (
            Array.from(
              select.options
            ).some(
              (option) =>
                option.value === sortBy
            )
          ) {
            select.value = sortBy;
          }
        });
    }

    shouldOpenOlfactiveFacetForQuery() {
      const query = String(this.query || "").trim();

      if (!query || !this.catalogProducts.length) {
        return false;
      }

      const analysis = this.analyzeQuery(query);
      const intentEntries = this.getUniqueEntries([
        ...(Array.isArray(analysis?.allIntentEntries)
          ? analysis.allIntentEntries
          : []),
        ...this.getSearchMapMatches(query, { allowPrefix: true })
      ]).filter((entry) => entry?.kind === "olfactive_character");

      if (!intentEntries.length) {
        return false;
      }

      /*
       * Åbn kun gruppen, hvis mindst én af de foreslåede karakterer
       * faktisk findes i det aktuelle produktkatalog. Selve filteret
       * forbliver uvalgt; åbningen er kun kontekstuel navigation.
       */
      return intentEntries.some((entry) => {
        return this.catalogProducts.some((product) => {
          return this.productMatchesIntentEntry(product, entry);
        });
      });
    }

    applyOlfactiveFacetQueryOpenState() {
      if (!this.shouldOpenOlfactiveFacetForQuery()) {
        return;
      }

      ["#FacetsWrapperDesktop", "#FacetsWrapperMobile"].forEach(
        (wrapperSelector) => {
          document.querySelectorAll(wrapperSelector).forEach((wrapper) => {
            Array.from(wrapper.children)
              .filter((element) =>
                element.matches("action-filter.js-filter")
              )
              .filter((filterElement) => {
                return Boolean(
                  filterElement.querySelector(
                    'input[name="filter.p.tag"]'
                  )
                );
              })
              .forEach((filterElement) => {
                const details = filterElement.querySelector("details");
                const title = filterElement.querySelector(".filter-title");
                const content = filterElement.querySelector(
                  ".filter-attribute.vertical-filter"
                );

                if (!details || !title || !content) {
                  return;
                }

                details.setAttribute("open", "");
                title.classList.remove("opened");
                content.classList.remove("bm-js-collapsible-hidden");
                content.style.display = "block";
                content.style.display = "block";
              });
          });
        }
      );
    }

    updateFacetInterface(params) {
      this.ensureOlfactiveFacetOptions();
      this.syncFacetControls(params);
      this.updateFacetCounts(params);
      this.updateActiveFacets(params);

      window.enforceEsenseraFacetOrder?.();

      window.applyEsenseraTypeSpecificFacetState?.(
        params.toString()
      );

      window.applyEsenseraDefaultFacetOpenState?.(
        params.toString()
      );

      this.applyOlfactiveFacetQueryOpenState();
    }

    updateFacetCounts(params) {
      const groups = [
        {
          role: "type",
          paramName:
            FILTER_PARAM_NAMES.type,
          inputs:
            ".js-type-option[data-type-handle]"
        },
        {
          role: "scent-profile",
          paramName:
            FILTER_PARAM_NAMES.scentProfile,
          inputs: `input[name="${FILTER_PARAM_NAMES.scentProfile}"]`
        },
        {
          role: "note",
          paramName:
            FILTER_PARAM_NAMES.note,
          inputs: `input[name="${FILTER_PARAM_NAMES.note}"]`
        },
        {
          role: "aromatherapy",
          paramName:
            FILTER_PARAM_NAMES.aromatherapy,
          inputs: `input[name="${FILTER_PARAM_NAMES.aromatherapy}"]`
        },
        {
          role: "organic",
          paramName:
            FILTER_PARAM_NAMES.organic,
          inputs: `input[name="${FILTER_PARAM_NAMES.organic}"]`
        },
        {
          role: "olfactive-character",
          paramName:
            FILTER_PARAM_NAMES.olfactive,
          inputs: `input[name="${FILTER_PARAM_NAMES.olfactive}"]`
        }
      ];

      groups.forEach((group) => {
        const baseParams =
          new URLSearchParams(params);

        /*
         * Almindelige facetgrupper viser antal ved at fjerne
         * deres eget aktive valg og bevare alle andre filtre.
         *
         * Olfactive character er bevidst strengere: valgte tags
         * skal stackes med AND — også inden for samme gruppe.
         * En ny mulighed beregnes derfor som:
         * øvrige aktive filtre + alle valgte tags + kandidaten.
         */
        const activeOlfactiveValues =
          group.role ===
            "olfactive-character"
            ? params.getAll(
                FILTER_PARAM_NAMES.olfactive
              )
            : [];

        baseParams.delete(
          group.paramName
        );

        const facetCatalog =
          this.catalogProducts.length
            ? this.catalogProducts
            : this.allProducts;

        const baseProducts =
          this.filterProducts(
            facetCatalog,
            baseParams
          );

        document
          .querySelectorAll(group.inputs)
          .forEach((element) => {
            if (group.role === "type") {
              const option = element;
              const input =
                option.querySelector(
                  ".js-type-collection-checkbox"
                );

              if (!input) {
                return;
              }

              const typeHandle =
                option.dataset.typeHandle ||
                "all";

              const count =
                typeHandle === "all"
                  ? baseProducts.length
                  : baseProducts.filter(
                      (product) =>
                        this.productMatchesType(
                          product,
                          typeHandle
                        )
                    ).length;

              this.applyFacetAvailability({
                input,
                count,
                role: group.role,
                option
              });

              return;
            }

            const input = element;

            let count;

            if (
              group.role ===
              "olfactive-character"
            ) {
              const candidateValue =
                input.value;

              const candidateIsActive =
                activeOlfactiveValues.some(
                  (value) =>
                    this.normalizeTerm(value) ===
                    this.normalizeTerm(
                      candidateValue
                    )
                );

              const stackedValues =
                candidateIsActive
                  ? activeOlfactiveValues
                  : [
                      ...activeOlfactiveValues,
                      candidateValue
                    ];

              count = baseProducts.filter(
                (product) =>
                  this.productMatchesValues(
                    product.tags,
                    stackedValues,
                    "all"
                  )
              ).length;
            } else {
              count = baseProducts.filter(
                (product) =>
                  this.productMatchesFacetValue(
                    product,
                    group.role,
                    input.value
                  )
              ).length;
            }

            this.applyFacetAvailability({
              input,
              count,
              role: group.role
            });
          });
      });
    }

    productMatchesFacetValue(
      product,
      role,
      value
    ) {
      switch (role) {
        case "scent-profile":
          return this.productMatchesValues(
            product.scentProfile,
            [value]
          );

        case "note":
          return this.productMatchesValues(
            product.perfumeryNote,
            [value]
          );

        case "aromatherapy":
          return this.productMatchesValues(
            product.aromatherapy,
            [value]
          );

        case "organic":
          return this.productMatchesValues(
            product.customProductBadge,
            [value]
          );

        case "olfactive-character":
          return this.productMatchesValues(
            product.tags,
            [value]
          );

        default:
          return false;
      }
    }

    applyFacetAvailability({
      input,
      count,
      role,
      option = null
    }) {
      const label =
        option ||
        input.closest(
          "label.facet-checkbox"
        );

      const listItem =
        label?.closest(
          ".facets__item"
        );

      const countElement =
        label?.querySelector(".count");

      if (countElement) {
        countElement.textContent =
          String(count);
      }

      const accessible =
        label?.querySelector(
          ".visually-hidden"
        );

      if (accessible) {
        const visibleLabel =
          label
            .querySelector(
              ".d-inline-flex.flex-1 span"
            )
            ?.textContent?.trim() ||
          input.value;

        accessible.textContent =
          `${visibleLabel} (${count} produkter)`;
      }

      const isAllType =
        role === "type" &&
        option?.dataset.typeHandle ===
          "all";

      /*
       * Olfactive character er en præcis, stackende tagfacet.
       * En inaktiv værdi uden match skal derfor ikke ghostes:
       * den skal helt ud af den aktuelle liste. En aktiv værdi
       * forbliver altid synlig, så brugeren kan fjerne den igen.
       */
      const shouldHideOlfactive =
        role === "olfactive-character" &&
        count === 0 &&
        !input.checked;

      const shouldDisable =
        role !== "olfactive-character" &&
        count === 0 &&
        !input.checked &&
        !isAllType;

      const filterElement =
        label?.closest(
          "action-filter.js-filter"
        );

      const isTypeBlocked =
        filterElement?.dataset
          .esenseraTypeBlocked ===
        "true";

      if (listItem) {
        listItem.classList.toggle(
          "bm-facet-option--disabled",
          shouldDisable || shouldHideOlfactive || isTypeBlocked
        );

        listItem.hidden =
          shouldHideOlfactive;

        listItem.style.display =
          shouldHideOlfactive
            ? "none"
            : "";

        listItem.style.opacity = "";
      }

      if (label) {
        label.style.opacity =
          shouldDisable &&
          !isTypeBlocked
            ? "0.35"
            : "";

        label.style.pointerEvents =
          shouldHideOlfactive ||
          shouldDisable ||
          isTypeBlocked
            ? "none"
            : "";

        label.style.cursor =
          shouldHideOlfactive ||
          shouldDisable ||
          isTypeBlocked
            ? "default"
            : "";
      }

      if (isTypeBlocked) {
        input.dataset
          .esenseraDisabledBeforeTypeBlock =
          shouldDisable ||
          shouldHideOlfactive
            ? "true"
            : "false";

        input.disabled = true;
      } else {
        input.disabled =
          shouldDisable ||
          shouldHideOlfactive;
      }

      if (
        shouldDisable ||
        shouldHideOlfactive
      ) {
        label?.setAttribute(
          "aria-disabled",
          "true"
        );
      } else {
        label?.removeAttribute(
          "aria-disabled"
        );
      }
    }

    updateActiveFacets(params) {
      const activeFilters = [];

      const activeTypeHandle =
        this.getActiveTypeHandle(params);

      if (activeTypeHandle !== "all") {
        activeFilters.push({
          paramName: FILTER_PARAM_NAMES.type,
          value:
            params.get(FILTER_PARAM_NAMES.type) ||
            TYPE_LABELS[activeTypeHandle],
          label:
            TYPE_LABELS[activeTypeHandle] ||
            activeTypeHandle
        });
      }

      [
        { paramName: FILTER_PARAM_NAMES.scentProfile },
        { paramName: FILTER_PARAM_NAMES.note },
        { paramName: FILTER_PARAM_NAMES.aromatherapy },
        {
          paramName: FILTER_PARAM_NAMES.organic,
          label: this.isDanish() ? "Økologisk" : "Organic"
        },
        { paramName: FILTER_PARAM_NAMES.olfactive }
      ].forEach(({ paramName, label }) => {
        params.getAll(paramName).forEach((value) => {
          activeFilters.push({
            paramName,
            value,
            label: label || value
          });
        });
      });

      const containers = this.root.querySelectorAll(
        "[data-esensera-active-facets]"
      );

      containers.forEach((container) => {
        const hasActiveFilters = activeFilters.length > 0;

        container.removeAttribute("data-esensera-query-active");
        container.hidden = !hasActiveFilters;
        container.classList.toggle("hidden", !hasActiveFilters);

        container.style.setProperty(
          "display",
          hasActiveFilters ? "flex" : "none",
          "important"
        );

        const fragment = document.createDocumentFragment();

        activeFilters.forEach((activeFilter) => {
          fragment.appendChild(
            this.createActiveFacetChip(activeFilter, params)
          );
        });

        if (activeFilters.length) {
          fragment.appendChild(this.createClearAllFacet(params));
        }

        container.replaceChildren(fragment);
      });
    }

    createSearchQueryChip(
      queryValue,
      params
    ) {
      const wrapper =
        document.createElement("div");

      wrapper.className =
        "facet-remove d-inline-flex align-center heading-color fs-small-1 lh-reset mt-15 mt-1025-0";

      const link =
        document.createElement("a");

      link.className =
        "py-10 active-facets__button active-facets__button--light remove-underline px-15 hover-as-fill-button accent-color-second-bg fs-small rounded-corner-item";

      link.href =
        this.buildRemoveSearchQueryUrl(
          params
        );

      link.setAttribute(
        "data-esensera-search-query-chip",
        ""
      );

      link.appendChild(
        document.createTextNode(
          `${
            this.isDanish()
              ? "Søgning"
              : "Search"
          }: ${queryValue}`
        )
      );

      const close =
        document.createElement("span");

      close.className =
        "ml-10 active-facets__button-inner button--tertiary";

      close.setAttribute(
        "aria-hidden",
        "true"
      );

      close.textContent = "×";

      link.appendChild(close);
      wrapper.appendChild(link);

      return wrapper;
    }

    createActiveFacetChip(
      activeFilter,
      params
    ) {
      const wrapper =
        document.createElement("div");

      wrapper.className =
        "facet-remove d-inline-flex align-center heading-color fs-small-1 lh-reset mt-15 mt-1025-0";

      const link =
        document.createElement("a");

      link.className =
        "py-10 active-facets__button active-facets__button--light remove-underline px-15 hover-as-fill-button accent-color-second-bg fs-small rounded-corner-item";

      link.href =
        this.buildRemoveFilterUrl(
          params,
          activeFilter.paramName,
          activeFilter.value
        );

      link.setAttribute(
        "data-esensera-custom-facet-url",
        ""
      );

      link.appendChild(
        document.createTextNode(
          activeFilter.label
        )
      );

      const close =
        document.createElement("span");

      close.className =
        "ml-10 active-facets__button-inner button--tertiary";

      close.setAttribute(
        "aria-hidden",
        "true"
      );

      close.textContent = "×";

      link.appendChild(close);
      wrapper.appendChild(link);

      return wrapper;
    }

    createClearAllFacet(params) {
      const wrapper =
        document.createElement("div");

      /*
       * Brug samme tekst og samme diskrete, understregede styling som
       * temaets eksisterende server-renderede "Fjern alle"-handling.
       * Dermed skifter knappen ikke navn eller udseende, når den
       * specialbyggede søgning overtager resultatvisningen.
       */
      wrapper.className =
        "py-10 mt-10 facet-remove facet-clear-all btn-text-transform fs-small d-inline-flex align-center heading-color lh-reset px-15 mt-1025-0";

      const link =
        document.createElement("a");

      link.className =
        "active-facets__button-remove underlined-link";

      link.href =
        this.buildClearFiltersUrl(
          params
        );

      link.setAttribute(
        "data-esensera-custom-facet-url",
        ""
      );

      link.textContent = this.isDanish()
        ? "Fjern alle"
        : "Remove all";

      wrapper.appendChild(link);

      return wrapper;
    }

    getCollectionContextDefinition(params) {
      const scentProfileValue = String(
        params.get(
          FILTER_PARAM_NAMES.scentProfile
        ) || ""
      ).trim();

      if (scentProfileValue) {
        const normalizedScentProfile =
          this.normalizeTerm(scentProfileValue);

        const scentProfileEntry =
          this.searchMap?.entries?.find((entry) => {
            if (entry.kind !== "scent_profile") {
              return false;
            }

            return [
              entry.filter_value,
              entry.label,
              entry.handle
            ].some(
              (value) =>
                this.normalizeTerm(value) ===
                normalizedScentProfile
            );
          });

        if (scentProfileEntry?.handle) {
          return {
            kind: "scent_profile",
            handle: scentProfileEntry.handle,
            label:
              scentProfileEntry.label ||
              scentProfileValue
          };
        }
      }

      const activeTypeHandle =
        this.getActiveTypeHandle(params);

      if (
        activeTypeHandle !== "all" &&
        TYPE_LABELS[activeTypeHandle]
      ) {
        return {
          kind: "type",
          handle: activeTypeHandle,
          label: TYPE_LABELS[activeTypeHandle]
        };
      }

      return null;
    }

    getCollectionContextUrl(handle) {
      const shopRoot =
        window.Shopify?.routes?.root || "/";

      const normalizedRoot = shopRoot.endsWith("/")
        ? shopRoot
        : `${shopRoot}/`;

      return `${normalizedRoot}collections/${handle}`;
    }

    clearCollectionContext() {
      ++this.collectionContextRequestId;
      this.collectionContextAbortController?.abort();
      this.collectionContextAbortController = null;

      this.root.removeAttribute(
        "data-esensera-has-collection-context"
      );

      if (!this.collectionContextContainer) {
        return;
      }

      this.collectionContextContainer.hidden = true;
      this.collectionContextContainer.removeAttribute(
        "aria-busy"
      );
      this.collectionContextContainer.replaceChildren();
    }

    async updateCollectionContext(params) {
      if (!this.collectionContextContainer) {
        return;
      }

      const definition =
        this.getCollectionContextDefinition(params);

      const requestId =
        ++this.collectionContextRequestId;

      this.collectionContextAbortController?.abort();
      this.collectionContextAbortController = null;

      if (!definition) {
        this.root.removeAttribute(
          "data-esensera-has-collection-context"
        );
        this.collectionContextContainer.hidden = true;
        this.collectionContextContainer.removeAttribute(
          "aria-busy"
        );
        this.collectionContextContainer.replaceChildren();
        return;
      }

      this.root.setAttribute(
        "data-esensera-has-collection-context",
        "true"
      );

      const cacheKey = definition.handle;
      const cachedMarkup =
        this.collectionContextCache.get(cacheKey);

      this.collectionContextContainer.hidden = false;
      this.collectionContextContainer.setAttribute(
        "aria-busy",
        "true"
      );

      if (cachedMarkup) {
        this.renderCollectionContextMarkup(
          cachedMarkup,
          definition
        );
        this.collectionContextContainer.removeAttribute(
          "aria-busy"
        );
        return;
      }

      this.collectionContextAbortController =
        new AbortController();

      try {
        const response = await fetch(
          this.getCollectionContextUrl(
            definition.handle
          ),
          {
            signal:
              this.collectionContextAbortController
                .signal,
            headers: {
              "X-Requested-With": "XMLHttpRequest"
            }
          }
        );

        if (!response.ok) {
          throw new Error(
            `Collection-header: HTTP ${response.status}`
          );
        }

        const responseText = await response.text();
        const parsedDocument =
          new DOMParser().parseFromString(
            responseText,
            "text/html"
          );

        const sourceBanner =
          parsedDocument.querySelector(
            "#MainContent .bls__colection-banner"
          ) ||
          parsedDocument.querySelector(
            ".bls__colection-banner"
          );

        if (!sourceBanner) {
          throw new Error(
            "Collection-headeren blev ikke fundet."
          );
        }

        const banner = sourceBanner.cloneNode(true);

        banner
          .querySelectorAll(
            ".page-title-breadcrumb, .breadcrumbs"
          )
          .forEach((breadcrumb) =>
            breadcrumb.remove()
          );

        const heading = banner.querySelector("h1");

        if (heading) {
          const replacementHeading =
            document.createElement("h2");

          Array.from(heading.attributes).forEach(
            (attribute) => {
              replacementHeading.setAttribute(
                attribute.name,
                attribute.value
              );
            }
          );

          replacementHeading.innerHTML =
            heading.innerHTML;

          heading.replaceWith(replacementHeading);
        }

        banner.setAttribute(
          "data-esensera-search-collection-banner",
          definition.handle
        );

        const markup = banner.outerHTML;

        this.collectionContextCache.set(
          cacheKey,
          markup
        );

        if (
          requestId !==
          this.collectionContextRequestId
        ) {
          return;
        }

        this.renderCollectionContextMarkup(
          markup,
          definition
        );
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        if (
          requestId !==
          this.collectionContextRequestId
        ) {
          return;
        }

        console.warn(
          "ESENSERA collection-header kunne ikke indlæses:",
          error
        );

        this.renderCollectionContextFallback(
          definition
        );
      } finally {
        if (
          requestId ===
          this.collectionContextRequestId
        ) {
          this.collectionContextContainer.removeAttribute(
            "aria-busy"
          );
        }
      }
    }

    renderCollectionContextMarkup(
      markup,
      definition
    ) {
      const template =
        document.createElement("template");

      template.innerHTML = String(markup || "").trim();

      this.collectionContextContainer.replaceChildren(
        template.content.cloneNode(true)
      );

      this.root.setAttribute(
        "data-esensera-has-collection-context",
        "true"
      );
      this.collectionContextContainer.hidden = false;
      this.collectionContextContainer.setAttribute(
        "data-esensera-collection-context-kind",
        definition.kind
      );
      this.collectionContextContainer.setAttribute(
        "data-esensera-collection-context-handle",
        definition.handle
      );

      if (
        typeof initLazyloadItem === "function"
      ) {
        initLazyloadItem();
      }

      if (
        typeof initializeScrollAnimationTrigger ===
        "function"
      ) {
        initializeScrollAnimationTrigger();
      }
    }

    renderCollectionContextFallback(definition) {
      const wrapper = document.createElement("div");
      wrapper.className =
        "esensera-search-collection-context__fallback container text-left";

      const heading = document.createElement("h2");
      heading.className =
        "heading-letter-spacing main-page-title h0";
      heading.textContent = definition.label;

      wrapper.appendChild(heading);
      this.collectionContextContainer.replaceChildren(
        wrapper
      );
      this.root.setAttribute(
        "data-esensera-has-collection-context",
        "true"
      );
      this.collectionContextContainer.hidden = false;
    }

    buildRemoveSearchQueryUrl(params) {
      const nextParams =
        new URLSearchParams(params);

      const activeTypeHandle =
        this.getActiveTypeHandle(
          nextParams
        );

      const scentProfileValue =
        nextParams
          .getAll(
            FILTER_PARAM_NAMES.scentProfile
          )
          .at(-1) || "";

      const scentProfileHandle =
        this.normalizeTerm(
          scentProfileValue
        ).replace(/\s+/g, "-");

      const validScentProfileHandles =
        new Set([
          "citrus",
          "floral",
          "aromatic",
          "fresh",
          "gourmand",
          "spicy",
          "resinous",
          "woody"
        ]);

      /*
       * På collection-sider skal Olfactive character ligge i
       * tagstien. Det giver AND-stacking mellem flere tags,
       * hvor storefront-filterværdier ellers kombineres med OR.
       */
      const olfactiveHandles = [
        ...new Set(
          nextParams
            .getAll(
              FILTER_PARAM_NAMES.olfactive
            )
            .map((value) =>
              this.handleizePathTag(value)
            )
            .filter(Boolean)
        )
      ];

      nextParams.delete(
        FILTER_PARAM_NAMES.olfactive
      );

      [
        "q",
        "options[prefix]",
        "options[fields]",
        "options[unavailable_products]",
        "type",
        "page",
        "section_id"
      ].forEach((paramName) => {
        nextParams.delete(paramName);
      });

      if (
        nextParams.get("sort_by") ===
        "relevance"
      ) {
        nextParams.delete("sort_by");
      }

      const shopRoot =
        window.Shopify?.routes?.root ||
        "/";

      const normalizedRoot =
        shopRoot.endsWith("/")
          ? shopRoot
          : `${shopRoot}/`;

      let targetPath;

      if (
        validScentProfileHandles.has(
          scentProfileHandle
        )
      ) {
        targetPath =
          `${normalizedRoot}collections/` +
          scentProfileHandle;
      } else if (
        activeTypeHandle !== "all"
      ) {
        targetPath =
          `${normalizedRoot}collections/` +
          activeTypeHandle;

        nextParams.delete(
          FILTER_PARAM_NAMES.type
        );
      } else {
        const hasProductFilters = [
          ...nextParams.keys()
        ].some((paramName) =>
          paramName.startsWith("filter.")
        );

        targetPath =
          hasProductFilters ||
          olfactiveHandles.length
            ? `${normalizedRoot}collections/all`
            : `${normalizedRoot}search`;

        if (
          !hasProductFilters &&
          !olfactiveHandles.length
        ) {
          nextParams.delete("sort_by");
        }
      }

      if (
        olfactiveHandles.length &&
        targetPath.includes(
          `${normalizedRoot}collections/`
        )
      ) {
        targetPath =
          `${targetPath.replace(/\/$/, "")}/` +
          olfactiveHandles.join("+");
      }

      const searchParams =
        nextParams.toString();

      return searchParams
        ? `${targetPath}?${searchParams}`
        : targetPath;
    }

    buildRemoveFilterUrl(
      params,
      paramName,
      valueToRemove
    ) {
      const nextParams =
        new URLSearchParams(params);

      const remainingValues =
        nextParams
          .getAll(paramName)
          .filter(
            (value) =>
              this.normalizeTerm(value) !==
              this.normalizeTerm(
                valueToRemove
              )
          );

      nextParams.delete(paramName);

      remainingValues.forEach((value) => {
        nextParams.append(
          paramName,
          value
        );
      });

      return this.buildSearchUrl(
        nextParams
      );
    }

    buildClearFiltersUrl(params) {
      const nextParams =
        new URLSearchParams(params);

      [
        FILTER_PARAM_NAMES.type,
        FILTER_PARAM_NAMES.scentProfile,
        FILTER_PARAM_NAMES.note,
        FILTER_PARAM_NAMES.aromatherapy,
        FILTER_PARAM_NAMES.organic,
        FILTER_PARAM_NAMES.olfactive,
        FILTER_PARAM_NAMES.priceMinimum,
        FILTER_PARAM_NAMES.priceMaximum
      ].forEach((paramName) => {
        nextParams.delete(paramName);
      });

      return this.buildSearchUrl(
        nextParams
      );
    }

    buildSearchUrl(params) {
      const searchParams =
        params.toString();

      return searchParams
        ? `${window.location.pathname}?${searchParams}`
        : window.location.pathname;
    }

    renderResults({
      products,
      pages,
      articles,
      hasProductSearchMatches =
        this.allProducts.length > 0
    }) {
      this.removeNativeResultGroups();

      let renderedPages = Array.isArray(pages)
        ? [...pages]
        : [];

      let renderedArticles =
        Array.isArray(articles)
          ? [...articles]
          : [];

      /*
       * Når den egentlige produktsøgning er tom, medtages også
       * Shopifys server-renderede side- og artikelresultater.
       * Kontrollerede indeksresultater står først, og URL-dubletter
       * fjernes.
       */
      if (!hasProductSearchMatches) {
        const mergedContent =
          this.mergeContentMatches(
            {
              pages: renderedPages,
              articles: renderedArticles
            },
            this.filterNativeContentMatches(
              this.query
            )
          );

        renderedPages = mergedContent.pages;
        renderedArticles =
          mergedContent.articles;

        renderedPages.push(
          this.getExploreProductsPage()
        );
      }

      if (products.length) {
        this.renderProducts(products);
      }

      if (renderedPages.length) {
        this.renderPages(renderedPages);
      }

      if (renderedArticles.length) {
        this.renderArticles(
          renderedArticles
        );
      }

      const total =
        products.length +
        renderedPages.length +
        renderedArticles.length;

      if (!total) {
        this.renderNoResults();
      }

      this.updateNoProductSearchState(
        hasProductSearchMatches
      );

      this.updatePageState(
        total,
        products.length
      );

      this.root.setAttribute(
        "data-esensera-custom-search-ready",
        "true"
      );

      document.dispatchEvent(
        new CustomEvent(
          "esensera:full-search-rendered",
          {
            detail: {
              query: this.query,
              productCount:
                products.length,
              pageCount:
                renderedPages.length,
              articleCount:
                renderedArticles.length,
              hasProductSearchMatches,
              searchParams:
                this.currentSearchParams.toString()
            }
          }
        )
      );
    }

    updateNoProductSearchState(
      hasProductSearchMatches
    ) {
      const hasNoProductSearchMatches =
        !hasProductSearchMatches;

      this.root.toggleAttribute(
        "data-esensera-no-product-results",
        hasNoProductSearchMatches
      );

      if (this.emptyStateContainer) {
        this.emptyStateContainer.hidden =
          !hasNoProductSearchMatches;
      }

      if (this.resultsStateContainer) {
        this.resultsStateContainer.hidden = false;
      }

      if (hasNoProductSearchMatches) {
        window.EsenseraSearchHistory?.render?.();
      }
    }

    removeNativeResultGroups() {
      this.productContainer
        .querySelectorAll(
          '[data-search-result-group], #product-grid, .template-search__results, .pagination-wrapper, nav.pagination'
        )
        .forEach((element) =>
          element.remove()
        );
    }

    renderProducts(products) {
      const fragment =
        this.productTemplate.content.cloneNode(
          true
        );

      const group =
        fragment.querySelector(
          "[data-esensera-custom-product-group]"
        );

      const grid =
        fragment.querySelector(
          "[data-esensera-custom-product-grid]"
        );

      products.forEach((product) => {
        if (product.card) {
          grid.appendChild(product.card);
        }
      });

      group.setAttribute(
        "data-esensera-product-count",
        products.length
      );

      this.productContainer.appendChild(
        fragment
      );
    }

    renderPages(items) {
      const group =
        document.createElement("div");

      group.className =
        "esensera-search-pages";

      group.setAttribute(
        "data-search-result-group",
        "pages"
      );

      group.setAttribute(
        "data-esensera-custom-content",
        "pages"
      );

      const isExploreOnly =
        items.length === 1 &&
        Boolean(items[0]?.isExploreProducts);

      if (isExploreOnly) {
        group.classList.add(
          "esensera-search-pages--explore-only"
        );
      }

      items.forEach((item) => {
        const card =
          document.createElement(
            "article"
          );

        card.className =
          item.isExploreProducts
            ? "esensera-search-page-card esensera-search-page-card--explore"
            : "esensera-search-page-card";

        const excerpt =
          this.createRichExcerpt(
            item.excerptHtml,
            160
          );

        if (item.isExploreProducts) {
          if (excerpt) {
            const excerptContainer =
              document.createElement("div");

            excerptContainer.className =
              "esensera-search-page-excerpt esensera-search-page-excerpt--explore rte";

            excerptContainer.appendChild(
              excerpt
            );

            card.appendChild(
              excerptContainer
            );
          }

          const link =
            document.createElement("a");

          link.href = item.url;
          link.className =
            "bm-text-link esensera-search-explore-link";

          const label =
            document.createElement("span");

          label.textContent =
            item.title || "";

          const arrow =
            document.createElement("span");

          arrow.className = "bm-link-arrow bm-link-arrow--right";
          arrow.setAttribute("aria-hidden", "true");
          arrow.innerHTML =
            '<svg class="bm-arrow bm-arrow--right" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 8h10M8.5 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="square" stroke-linejoin="miter"></path></svg>';

          link.append(label, arrow);
          card.appendChild(link);
          group.appendChild(card);
          return;
        }

        const title =
          document.createElement("h3");

        title.className =
          "esensera-search-page-title m-0 heading-color";

        const link =
          document.createElement("a");

        link.href = item.url;
        link.className =
          "full-unstyled-link";
        link.textContent =
          item.title || "";

        title.appendChild(link);
        card.appendChild(title);

        if (excerpt) {
          const excerptContainer =
            document.createElement("div");

          excerptContainer.className =
            "esensera-search-page-excerpt rte";

          excerptContainer.appendChild(
            excerpt
          );

          card.appendChild(
            excerptContainer
          );
        }

        group.appendChild(card);
      });

      this.productContainer.appendChild(
        group
      );
    }

    renderArticles(items) {
      const group =
        document.createElement("div");

      group.className =
        "esensera-search-articles";

      group.setAttribute(
        "data-search-result-group",
        "articles"
      );

      group.setAttribute(
        "data-esensera-custom-content",
        "articles"
      );

      const grid =
        document.createElement("div");

      grid.className =
        "esensera-search-article-grid";

      items.forEach((item) => {
        const card =
          document.createElement(
            "article"
          );

        card.className =
          "esensera-search-article-card";

        const link =
          document.createElement("a");

        link.href = item.url;

        link.className =
          "full-unstyled-link";

        if (item.imageUrl) {
          const media =
            document.createElement("div");

          media.className =
            "esensera-search-article-media";

          const image =
            document.createElement("img");

          image.src = item.imageUrl;
          image.alt = item.title || "";
          image.loading = "lazy";

          image.className =
            "esensera-search-article-image";

          media.appendChild(image);
          link.appendChild(media);
        }

        const title =
          document.createElement("h3");

        title.className =
          "esensera-search-article-title m-0 heading-color";

        title.textContent =
          item.title || "";

        link.appendChild(title);
        card.appendChild(link);
        grid.appendChild(card);
      });

      group.appendChild(grid);

      this.productContainer.appendChild(
        group
      );
    }

    createRichExcerpt(
      html,
      maximumCharacters
    ) {
      const source =
        document.createElement("div");

      source.innerHTML = String(
        html || ""
      ).trim();

      const firstParagraph =
        source.querySelector("p") ||
        source.firstElementChild;

      if (!firstParagraph) {
        return null;
      }

      const clone =
        firstParagraph.cloneNode(true);

      const walker =
        document.createTreeWalker(
          clone,
          NodeFilter.SHOW_TEXT
        );

      let remaining =
        maximumCharacters;

      let node;

      while (
        (node = walker.nextNode())
      ) {
        const text =
          node.nodeValue || "";

        if (remaining <= 0) {
          node.nodeValue = "";
          continue;
        }

        if (text.length > remaining) {
          node.nodeValue =
            `${text
              .slice(0, remaining)
              .trimEnd()}…`;

          remaining = 0;
          continue;
        }

        remaining -= text.length;
      }

      return clone;
    }

    renderNoResults() {
      const empty =
        document.createElement("div");

      empty.className =
        "template-search__results collection collection--empty page-width";

      empty.setAttribute(
        "data-esensera-custom-empty",
        ""
      );

      const wrapper =
        document.createElement("div");

      wrapper.className =
        "text-center title-wrapper";

      const heading =
        document.createElement("h2");

      heading.className =
        "title title--primary";

      heading.textContent =
        this.config.noResultsText ||
        (this.isDanish()
          ? "Ingen resultater blev fundet."
          : "No results were found.");

      wrapper.appendChild(heading);
      empty.appendChild(wrapper);

      this.productContainer.appendChild(
        empty
      );
    }

    renderError() {
      /*
       * Fejltilstanden må ikke blandes med Shopifys native produkter,
       * discovery-blokke eller en tom "Fjern alle"-række. Det var netop
       * den selvmodsigende tilstand, der gjorde en tidligere 5.26 ubrugelig.
       */
      this.removeNativeResultGroups();

      this.productContainer
        .querySelectorAll("[data-esensera-custom-error]")
        .forEach((element) => element.remove());

      if (this.emptyStateContainer) {
        this.emptyStateContainer.hidden = true;
      }

      if (this.resultsStateContainer) {
        this.resultsStateContainer.hidden = false;
      }

      this.updateActiveFacets(
        this.currentSearchParams instanceof URLSearchParams
          ? this.currentSearchParams
          : new URLSearchParams()
      );

      const message =
        document.createElement("p");

      message.className =
        "text-center";

      message.setAttribute(
        "role",
        "alert"
      );

      message.setAttribute(
        "data-esensera-custom-error",
        ""
      );

      message.textContent =
        this.config.errorText ||
        (this.isDanish()
          ? "Søgningen kunne ikke indlæses. Prøv igen."
          : "The search could not be loaded. Please try again.");

      this.productContainer.prepend(
        message
      );
    }

    updatePageState(
      totalCount,
      productCount
    ) {
      this.root.classList.toggle(
        "template-search--empty",
        totalCount === 0
      );

      const title =
        this.root.querySelector(
          ".page-title h1"
        );

      if (title) {
        const hasQuery =
          String(this.query || "").trim().length > 0;

        if (hasQuery && productCount === 0) {
          title.textContent = this.isDanish()
            ? "Din søgning matcher ingen produkter"
            : "Your search matches no products";
        } else if (
          totalCount > 0 &&
          this.config.resultsTitleText
        ) {
          title.textContent =
            this.config.resultsTitleText;
        }
      }

      this.root
        .querySelectorAll(
          "#ProductCountDesktop, #ProductCount"
        )
        .forEach((element) => {
          const noun = this.isDanish()
            ? productCount === 1
              ? "produkt"
              : "produkter"
            : productCount === 1
              ? "product"
              : "products";

          element.textContent =
            `${productCount} ${noun}`;
        });

      /*
       * Shopify bygger dokumenttitlen ud fra den native søgetælling,
       * før den specialbyggede titel-, synonym- og facetlogik er kørt.
       * Opdatér derfor browsertabben med præcis det samme produktantal,
       * som den færdige resultatvisning viser.
       */
      this.updateSearchDocumentTitle(
        productCount
      );
    }

    updateSearchDocumentTitle(
      productCount
    ) {
      const query = String(
        this.query || ""
      )
        .replace(/\s+/g, " ")
        .trim();

      if (!query) {
        return;
      }

      const count = Math.max(
        0,
        Number(productCount) || 0
      );

      const currentTitle = String(
        document.title || ""
      );

      /*
       * Bevar butikkens eksisterende suffiks, eksempelvis
       * " – Esensera", uden at butiknavnet hardcodes i JavaScript.
       */
      const suffixMatch =
        currentTitle.match(
          /\s+[–—-]\s+[^–—-]+$/u
        );

      const suffix = suffixMatch
        ? suffixMatch[0]
        : "";

      const language = String(
        document.documentElement.lang || ""
      ).toLowerCase();

      let resultText = "";

      if (language.startsWith("da")) {
        if (count === 0) {
          resultText =
            `Ingen resultater fundet for “${query}”`;
        } else if (count === 1) {
          resultText =
            `1 resultat fundet for “${query}”`;
        } else {
          resultText =
            `${count} resultater fundet for “${query}”`;
        }

        document.title =
          `Søgning: ${resultText}${suffix}`;
        return;
      }

      if (language.startsWith("en")) {
        if (count === 0) {
          resultText =
            `No results found for “${query}”`;
        } else if (count === 1) {
          resultText =
            `1 result found for “${query}”`;
        } else {
          resultText =
            `${count} results found for “${query}”`;
        }

        document.title =
          `Search: ${resultText}${suffix}`;
        return;
      }

      /*
       * Fallback for øvrige sprog: behold den server-renderede ordlyd,
       * men synkronisér antal og den aktuelle søgestreng.
       */
      const titleBody = suffix
        ? currentTitle.slice(
            0,
            -suffix.length
          )
        : currentTitle;

      let updatedTitleBody =
        titleBody.replace(
          /\b\d+\b/u,
          String(count)
        );

      updatedTitleBody =
        updatedTitleBody.replace(
          /[“"][^”"]*[”"]/u,
          `“${query}”`
        );

      document.title =
        `${updatedTitleBody}${suffix}`;
    }

    setLoading(isLoading) {
      this.root.toggleAttribute(
        "data-esensera-search-loading",
        isLoading
      );

      this.form?.classList.toggle(
        "loading",
        isLoading
      );
    }

    isDanish() {
      return document.documentElement.lang
        .toLowerCase()
        .startsWith("da");
    }
  }

  const initialise = () => {
    document
      .querySelectorAll(
        ".template-search:not([data-esensera-full-search-initialised])"
      )
      .forEach((root) => {
        root.setAttribute(
          "data-esensera-full-search-initialised",
          ""
        );

        new EsenseraFullSearch(
          root
        ).init();
      });
  };

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initialise
    );
  } else {
    initialise();
  }
})();
